/**
 * @fileoverview Compose a lead's pipeline, evidence, outreach, and operational context inside `IntakeLeadDetail.js`. Focused modules own documents, activity, agent findings, run inspection, and outreach editing; this layer coordinates stage-oriented presentation and explicit controls. Backend projections own stage/run/spend facts and backend actions own admission. Local detail-visibility preferences do not change execution or turn AI recommendations into staff decisions.
 */

import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AppTab, AppTabView } from '../shared';
import ToastContext from '../../ToastContext';
import ConflictCheckPanel from '../ConflictChecks/ConflictCheckPanel';
import useConflictChecksEnabled from '../ConflictChecks/useConflictChecksEnabled';
import {
  continueInformationEvaluationReview,
  createIntakeOutreach,
  fetchIntakeOutreach,
  resolveIntakeStaffReview,
  runIntakePipeline,
  runIntakeAgent,
  runIntakeStage,
  sendIntakeOutreach,
  skipIntakeStage,
  updateLeadPipelineVersion,
  updateIntakeOutreach,
} from './intakeApi';
import {
  EmptyState,
  IntakeBadge,
  SelectInput,
  TextArea,
  TextInput } from
'./IntakeDetailPrimitives';
import { formatDate, formatPercent, INFORMATION_REQUEST_STAGE_KEYS, getActivityDetail, getRecordStatusClass, getRunPipelineActionLabel, getRunPipelineActionTitle, getRunTime, getStageLabel, getTimestamp, pluralize, STAGE_OPTIONS, titleize } from './intakeFormatters';
import {
  ACTIVE_STATUSES,
  ATTENTION_STATUSES,
  CLEARED_STATUSES,
  CLOSED_LEAD_STATUSES,
  COMPLETE_STATUSES,
  DEDUPE_REVIEW_TITLE,
  EDITABLE_OUTREACH_STATUSES,
  FAILED_RUN_STATUSES,
  FINAL_RECOMMENDATION_HUMAN_REVIEW_TOKENS,
  FINAL_RECOMMENDATION_NEEDS_INFO_TOKENS,
  FINAL_RECOMMENDATION_NOT_VIABLE_TOKENS,
  FINAL_RECOMMENDATION_VIABLE_TOKENS,
  getRunStatusTone,
  NO_ACTION_RUN_STATUSES,
  QUEUED_RUN_STATUSES,
  REVIEW_RUN_STATUSES,
  RUNNING_RUN_STATUSES,
  SEND_BLOCKED_OUTREACH_STATUSES,
  TERMINAL_NON_PROGRESS_STATUSES,
  UNRESOLVED_DUPLICATE_STATUSES } from
'./intakeStatuses';
import { formatStageValue, FormattedAgentOutput, LegalAuthorityFindingChips, LegalAuthorityFindingDetailModal, LegalSourceChips, LegalSourceDetailModal, parseJsonValue, STAGE_TIMELINE_HIDDEN_OUTPUT_KEYS } from './IntakeAgentOutput';
import { AgentFeedbackModal, AgentRunDetailModal, useAgentFeedbackModal, useAgentRunDetailModal } from './IntakeAgentRunModals';
import { buildActivityItems } from './intakeActivity';
import { RunLogsTab, SourceEventsTab } from './IntakeRecordTabs';
import { DocumentsTab } from './IntakeDocumentsTab';
import { buildOutreachForm, buildOutreachPayload, buildStageActionItems, getDefaultOutreachForm, getDraftSubtitle, getOutreachDeliveryNotice, getOutreachSendToast } from './intakeOutreach';
import { InformationRequestMonitoringPanel, WorkflowRuntimeSection } from './Pipeline';
import IntakeActivityWorkspace from './IntakeActivityWorkspace';
import EngagementDraftPreview from './EngagementDraftPreview';
import AppIcon from "../shared/AppIcon";

export { DecisionActionButtons, IntakeDecisionModal } from './Decision';

const STAGE_DESCRIPTIONS = {
  ingest_dedupe: 'Lead received, duplicate checks, source data normalized.',
  triage: 'Category, jurisdiction, and basic eligibility signals.',
  analysis: 'Claim analysis and risk review notes.',
  specialist_intake_review: 'Selected claim experts determine claim-specific gaps and request scope.',
  class_action_detection: 'Screen specialist intake outputs for potential class action indicators.',
  info_gathering: 'Missing information and prospect outreach.',
  information_request: 'Missing information and prospect outreach.',
  outreach_response_monitor: 'Polling for replies, uploaded documents, and no-response review.',
  document_review: 'Requested documents and review status.',
  information_evaluation: 'Evaluate whether responses/documents satisfy the request.',
  specialist_evaluation: 'Selected claim experts evaluate legal viability after requested information is complete.',
  final_brief: 'Attorney-facing summary prepared for decision.',
  decision: 'Accept, reject, duplicate, or archive.',
  conversion: 'Engagement-letter handoff, then matter conversion after Pipedrive is won.'
};

const RUN_ALL_STOP_STAGE_KEYS = new Set(['decision', 'conversion']);
const FINAL_EVAL_AGENT_KEYS = new Set(['final_eval', 'final_evaluation', 'final_brief', 'final_recommendation']);
const FINAL_EVAL_AGENT_ROLES = new Set(['final_eval', 'final_evaluation', 'final_recommendation']);
const ACTIVE_RUN_REFRESH_INTERVAL_MS = 2500;
const CLAIM_SPECIALIST_RUN_STAGE_KEYS = new Set(['specialist_intake_review', 'specialist_evaluation']);
const OUTREACH_RESPONSE_MONITOR_STAGE_KEY = 'outreach_response_monitor';
const INFORMATION_REQUEST_LOOP_STAGE_KEYS = new Set([
  'info_gathering',
  'information_request',
  OUTREACH_RESPONSE_MONITOR_STAGE_KEY,
  'document_review',
  'information_evaluation'
]);
const MANUAL_SKIPPABLE_STAGE_KEYS = new Set([...INFORMATION_REQUEST_STAGE_KEYS, 'class_action_detection', 'specialist_intake_review']);
const STAFF_REVIEW_RESOLUTION_BLOCKED_STAGE_KEYS = new Set([
  ...INFORMATION_REQUEST_LOOP_STAGE_KEYS,
  ...RUN_ALL_STOP_STAGE_KEYS
]);
const INTAKE_SPENDING_VIEW_STORAGE_KEY = 'intake.pipeline.showSpending';
const INTAKE_LEGAL_VERIFICATION_VIEW_STORAGE_KEY = 'intake.pipeline.showLegalVerification';
const INTAKE_AGENT_CONTROLS_VIEW_STORAGE_KEY = 'intake.pipeline.showAgentControls';
const INTAKE_RUNTIME_DETAIL_VIEW_STORAGE_KEY = 'intake.pipeline.showRuntimeDetail';
const STAGE_HEADER_STATE_LIMIT = 4;
const STAGE_HEADER_MORE_INFO_KEYS = new Set([
  'client_info_needed',
  'documents_requested',
  'followup_items',
  'information_items',
  'items_not_received',
  'missing_documents',
  'missing_information',
  'unresolved_gaps'
]);
const STAGE_HEADER_SIGNAL_KEYS = new Set([
  'automation_can_continue',
  'decision',
  'flag_for_human_review',
  'flag_reasons',
  'human_review_required',
  'needs_more_info',
  'recommendation',
  'requested_information_status',
  'review_gate',
  'risk_flags',
  ...STAGE_HEADER_MORE_INFO_KEYS
]);



function readSpendingViewPreference() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(INTAKE_SPENDING_VIEW_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}



function writeSpendingViewPreference(value) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(INTAKE_SPENDING_VIEW_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Ignore storage errors so private browsing modes do not break the pipeline UI.
  }
}



function readLegalVerificationViewPreference() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(INTAKE_LEGAL_VERIFICATION_VIEW_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}



function readAgentControlsViewPreference() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(INTAKE_AGENT_CONTROLS_VIEW_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}



function writeAgentControlsViewPreference(value) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(INTAKE_AGENT_CONTROLS_VIEW_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Ignore storage errors so private browsing modes do not break the pipeline UI.
  }
}



function readRuntimeDetailViewPreference() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(INTAKE_RUNTIME_DETAIL_VIEW_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}



function writeRuntimeDetailViewPreference(value) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(INTAKE_RUNTIME_DETAIL_VIEW_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Ignore storage errors so private browsing modes do not break the pipeline UI.
  }
}



function writeLegalVerificationViewPreference(value) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(INTAKE_LEGAL_VERIFICATION_VIEW_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Ignore storage errors so private browsing modes do not break the pipeline UI.
  }
}



function formatInteger(value) {
  return new Intl.NumberFormat('en-US').format(Number(value || 0));
}



function formatEstimatedCost(value, currency = 'usd') {
  const amount = Number(value || 0);
  const minimumFractionDigits = amount > 0 && amount < 1 ? 4 : 2;
  const maximumFractionDigits = amount > 0 && amount < 1 ? 4 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: String(currency || 'usd').toUpperCase(),
    minimumFractionDigits,
    maximumFractionDigits
  }).format(amount);
}



function formatAiSpendingActivity(spending) {
  const runCount = Number(spending?.agent_run_count || 0);
  const tokenCount = Number(spending?.total_tokens || 0);
  if (!runCount) return 'No recorded AI runs';
  return `${formatInteger(runCount)} agent run${runCount === 1 ? '' : 's'} / ${formatInteger(tokenCount)} token${tokenCount === 1 ? '' : 's'}`;
}



function formatAiProviderBreakdown(spending) {
  const paidRows = (spending?.by_provider || []).filter((row) => Number(row?.estimated_cost || 0) > 0);
  if (!paidRows.length) return 'No provider spend';
  return paidRows.slice(0, 3).map((row) =>
    `${getModelProviderLabel(row.provider) || 'Unknown'} ${formatEstimatedCost(row.estimated_cost, spending?.currency || 'usd')}`
  ).join(' / ');
}



function normalizeRecommendationToken(value) {
  return String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}



function getRecommendationTimestamp(recommendation) {
  return getTimestamp(recommendation?.created_at || recommendation?.updated_at);
}



function getLatestFinalRecommendation(finalRecommendations = []) {
  return [...(finalRecommendations || [])]
  .filter(Boolean)
  .sort((left, right) => {
    const timeDiff = getRecommendationTimestamp(right) - getRecommendationTimestamp(left);
    return timeDiff || Number(right.id || 0) - Number(left.id || 0);
  })[0] || null;
}



function getFinalRecommendationToken(recommendation) {
  return normalizeRecommendationToken(recommendation?.classification) || normalizeRecommendationToken(recommendation?.recommended_action);
}



function getFinalRecommendationDisplay(recommendation) {
  const token = getFinalRecommendationToken(recommendation);
  if (FINAL_RECOMMENDATION_NOT_VIABLE_TOKENS.has(token)) return { label: 'Not Viable', tone: 'danger' };
  if (FINAL_RECOMMENDATION_NEEDS_INFO_TOKENS.has(token)) return { label: 'Needs More Info', tone: 'warning' };
  if (FINAL_RECOMMENDATION_HUMAN_REVIEW_TOKENS.has(token)) return { label: 'Human Review', tone: 'info' };
  if (FINAL_RECOMMENDATION_VIABLE_TOKENS.has(token)) return { label: 'Viable', tone: 'success' };
  if (token === 'duplicate') return { label: 'Duplicate', tone: 'warning' };
  return { label: recommendation ? titleize(token) : '', tone: 'neutral' };
}



function getFinalRecommendationActionLabel(recommendation) {
  const token = normalizeRecommendationToken(recommendation?.recommended_action);
  return token ? titleize(token) : '';
}



function getFinalRecommendationConfidenceLabel(recommendation) {
  if (recommendation?.confidence === null || recommendation?.confidence === undefined || recommendation?.confidence === '') return '';
  return `Confidence ${formatPercent(recommendation.confidence)}`;
}



function buildDecisionBrief(nextAction, finalRecommendation) {
  const display = getFinalRecommendationDisplay(finalRecommendation);
  if (!finalRecommendation) {
    return {
      hasRecommendation: false,
      title: nextAction.title,
      detail: nextAction.detail,
      tone: 'neutral',
      meta: 'Review the current pipeline state before continuing.'
    };
  }
  const actionLabel = getFinalRecommendationActionLabel(finalRecommendation);
  return {
    hasRecommendation: true,
    title: display.label || 'Final Recommendation',
    detail: finalRecommendation.summary || nextAction.detail,
    tone: display.tone,
    meta: [
    actionLabel ? `Recommended action: ${actionLabel}` : '',
    getFinalRecommendationConfidenceLabel(finalRecommendation),
    nextAction?.title ? `Next: ${nextAction.title}` : '']
    .filter(Boolean)
    .join(' / ')
  };
}



function getConfiguredStageOptions(pipelineStages) {
  const configured = (pipelineStages || [])
  .filter((stage) => stage?.key && stage.enabled !== false)
  .sort((left, right) => (Number(left.order) || 0) - (Number(right.order) || 0))
  .map((stage) => [stage.key, stage.label || titleize(stage.key), stage]);
  return configured.length ? configured : STAGE_OPTIONS.map(([key, label]) => [key, label, { key, label, description: STAGE_DESCRIPTIONS[key], agents: [] }]);
}



function isPlaceholderModelId(modelId) {
  const value = String(modelId || '').trim();
  return !value || value.startsWith('configure-');
}



function getModelProviderLabel(provider) {
  const value = String(provider || '').trim().toLowerCase();
  if (value === 'openai') return 'OpenAI';
  if (value === 'anthropic') return 'Anthropic';
  return titleize(value);
}



function getModelProfileOptionLabel(profile) {
  if (!profile) return 'Default model';
  const name = profile.display_name || titleize(profile.tier_key);
  return [profile.model_id, name, getModelProviderLabel(profile.provider), titleize(profile.tier_key)].filter(Boolean).join(' / ');
}



function getModelProfileRunLabel(profile) {
  if (!profile) return 'Default model';
  const name = profile.display_name || titleize(profile.tier_key);
  return [name, getModelProviderLabel(profile.provider), profile.model_id].filter(Boolean).join(' / ');
}



function getManualModelProfileOptions(modelProfiles = []) {
  return (modelProfiles || [])
  .filter((profile) => profile?.active && !isPlaceholderModelId(profile.model_id))
  .map((profile) => ({
    value: String(profile.id),
    label: getModelProfileOptionLabel(profile),
    title: profile.model_id || profile.display_name || titleize(profile.tier_key),
    meta: [profile.display_name || titleize(profile.tier_key), getModelProviderLabel(profile.provider), titleize(profile.tier_key)].filter(Boolean).join(' / '),
    model: '',
  }));
}



function getModelProfileById(modelProfiles = [], modelProfileId) {
  const selectedId = String(modelProfileId || '');
  if (!selectedId) return null;
  return (modelProfiles || []).find((profile) => String(profile.id) === selectedId) || null;
}



function getStageModelSelectionKey(stageKey) {
  return `${stageKey || ''}:__stage__`;
}



function ModelProfilePicker({
  id,
  label,
  options,
  value,
  disabled = false,
  title = '',
  onChange = () => {}
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const normalizedValue = String(value || '');
  const selectedOption = options.find((option) => option.value === normalizedValue) || options[0] || null;
  const safeId = String(id || label || 'model-profile-picker').replace(/[^a-zA-Z0-9_-]/g, '-');

  const updateMenuPosition = useCallback(

  () => {
    if (!triggerRef.current || typeof window === 'undefined') return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuRect = menuRef.current?.getBoundingClientRect();
    const gap = 8;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const menuWidth = Math.min(448, Math.max(0, viewportWidth - gap * 2));
    const left = Math.max(gap, Math.min(triggerRect.left, viewportWidth - menuWidth - gap));
    const spaceAbove = Math.max(0, triggerRect.top - gap * 2);
    const spaceBelow = Math.max(0, viewportHeight - triggerRect.bottom - gap * 2);
    const preferredHeight = Math.min(288, Math.max(160, Math.max(spaceAbove, spaceBelow)));
    const renderAbove = spaceAbove >= Math.min(180, menuRect?.height || preferredHeight) || spaceAbove >= spaceBelow;
    const maxHeight = Math.min(288, Math.max(140, renderAbove ? spaceAbove : spaceBelow));
    const measuredHeight = Math.min(menuRect?.height || maxHeight, maxHeight);
    const top = renderAbove ?
      Math.max(gap, triggerRect.top - gap - measuredHeight) :
      Math.max(gap, Math.min(triggerRect.bottom + gap, viewportHeight - measuredHeight - gap));
    setMenuStyle({
      left: `${left}px`,
      maxHeight: `${maxHeight}px`,
      top: `${top}px`,
      width: `${menuWidth}px`,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    return undefined;
  }, [open, options.length, updateMenuPosition]);

  useEffect(

  () => {
    if (!open) return undefined;
    const handlePointerDown =

    (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown =

    (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const handleReposition =

    () => updateMenuPosition();
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return   () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updateMenuPosition]);

  const toggleOpen =

  () => {
    if (!disabled) setOpen((current) => !current);
  };

  const selectOption =

  (optionValue) => {
    onChange(optionValue);
    setOpen(false);
  };

  const menu = open && typeof document !== 'undefined' ? (
    <div
      ref={menuRef}
      className="intake-model-picker__menu"
      role="listbox"
      aria-labelledby={`${safeId}-label`}
      style={menuStyle || { visibility: 'hidden' }}>
      {options.map((option) => {
        const selected = option.value === normalizedValue;
        return (
          <button
            key={option.value || 'pipeline-defaults'}
            type="button"
            role="option"
            aria-selected={selected}
            className={`intake-model-picker__option${selected ? ' intake-model-picker__option--selected' : ''}`}
            onClick={() => selectOption(option.value)}>
            <span className="intake-model-picker__option-main">
              <strong>{option.title}</strong>
              {selected && <AppIcon icon="pi pi-check" aria-hidden="true" />}
            </span>
            {option.meta && <span className="intake-model-picker__option-meta">{option.meta}</span>}
            {option.model && <code>{option.model}</code>}
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`intake-stage-agent-controls__model${disabled ? ' intake-stage-agent-controls__model--disabled' : ''}${open ? ' intake-stage-agent-controls__model--open' : ''}`}
    >
      <span id={`${safeId}-label`} className="intake-stage-agent-controls__model-label">{label}</span>
      <div className="intake-model-picker">
        <button
          ref={triggerRef}
          type="button"
          className="intake-model-picker__trigger"
          aria-label={label}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-describedby={`${safeId}-value`}
          disabled={disabled}
          title={title}
          onClick={toggleOpen}>
          <span id={`${safeId}-value`} className="intake-model-picker__selected">
            <strong>{selectedOption?.title || 'Pipeline defaults'}</strong>
            <small>{selectedOption?.model || selectedOption?.meta || 'Use configured pipeline models'}</small>
          </span>
          <AppIcon icon="pi pi-chevron-down" aria-hidden="true" />
        </button>
      </div>
      {menu && createPortal(menu, document.body)}
    </div>
  );
}



function WorkflowBadge({ status }) {
  return <IntakeBadge value={status} className={getRecordStatusClass(status)} />;
}



function getLatestByStage(stageRuns) {
  const latestByStage = stageRuns.reduce(

  (latest, run) => {
    const current = latest[run.stage_key];
    const runTime = getTimestamp(getRunTime(run)) || run.id || 0;
    const currentTime = current ? getTimestamp(getRunTime(current)) || current.id || 0 : -1;
    if (!current || runTime >= currentTime) {
      latest[run.stage_key] = run;
    }
    return latest;
  }, {});
  Object.keys(latestByStage).forEach((stageKey) => {
    if (CLEARED_STATUSES.has(latestByStage[stageKey]?.status)) {
      delete latestByStage[stageKey];
    }
  });
  return latestByStage;
}



function getStageConfig(stageOptions, stageKey, run = null) {
  if (stageKey === OUTREACH_RESPONSE_MONITOR_STAGE_KEY) {
    return {
      key: stageKey,
      label: 'Waiting for Reply',
      config: {
        key: stageKey,
        label: 'Waiting for Reply',
        description: STAGE_DESCRIPTIONS[stageKey],
        agents: []
      }
    };
  }
  const option = stageOptions.find(([key]) => key === stageKey);
  if (option) {
    return {
      key: option[0],
      label: option[1],
      config: option[2] || {}
    };
  }
  const label = run?.stage_label || getStageLabel(stageKey);
  return {
    key: stageKey,
    label,
    config: {
      key: stageKey,
      label,
      description: STAGE_DESCRIPTIONS[stageKey] || 'Recorded intake pipeline stage.',
      agents: []
    }
  };
}



function getNextConfiguredStage(stageOptions, stageKey) {
  const currentIndex = stageOptions.findIndex(([key]) => key === stageKey);
  if (currentIndex < 0) return null;
  const nextOption = stageOptions.slice(currentIndex + 1).find(([key]) => key);
  if (!nextOption) return null;
  return {
    key: nextOption[0],
    label: nextOption[1] || getStageLabel(nextOption[0]),
    config: nextOption[2] || {}
  };
}



function getOutreachMonitorDescription(lead = {}) {
  const monitoring = lead?.information_request_monitoring || {};
  if (monitoring.non_live_delivery) {
    return 'Non-live delivery recorded. Prospect reply polling starts after a live send.';
  }
  if (monitoring.state === 'received') {
    return 'A reply or uploaded document has been detected for this request.';
  }
  return STAGE_DESCRIPTIONS[OUTREACH_RESPONSE_MONITOR_STAGE_KEY];
}



function sortStageRunsChronologically(stageRuns = []) {
  return [...stageRuns]
  .filter((run) => run?.stage_key && !CLEARED_STATUSES.has(run.status))
  .sort(

  (left, right) => {
    const leftTime = getTimestamp(getRunTime(left));
    const rightTime = getTimestamp(getRunTime(right));
    if (leftTime && rightTime && leftTime !== rightTime) return leftTime - rightTime;
    return Number(left.id || 0) - Number(right.id || 0);
  });
}



function getInferredDocumentBypassRows(stageOptions, stageRuns, effectiveCurrentStageKey, lead) {
  if (effectiveCurrentStageKey !== 'document_review') return [];
  if (Number(lead?.pending_document_review_count || 0) <= 0) return [];
  const hasInformationRequestRun = (stageRuns || []).some((run) => INFORMATION_REQUEST_STAGE_KEYS.includes(run?.stage_key));
  if (hasInformationRequestRun) return [];
  const documentReviewIndex = stageOptions.findIndex(([stageKey]) => stageKey === 'document_review');
  if (documentReviewIndex < 0) return [];
  const informationRequestOption = stageOptions
  .slice(0, documentReviewIndex)
  .reverse()
  .find(([stageKey]) => INFORMATION_REQUEST_STAGE_KEYS.includes(stageKey));
  if (!informationRequestOption) return [];
  const [stageKey, label] = informationRequestOption;
  return [{
    rowKey: `inferred-document-bypass:${stageKey}`,
    stageKey,
    synthetic: true,
    run: {
      id: null,
      stage_key: stageKey,
      stage_label: label,
      status: 'skipped',
      output_summary_json: JSON.stringify({
        summary: `${label} skipped because documents need review.`,
        skipped: true,
        next_stage: 'document_review'
      })
    }
  }];
}



function getInferredDedupeRows(stageOptions, stageRuns, effectiveCurrentStageKey, lead) {
  if (effectiveCurrentStageKey === 'ingest_dedupe') return [];
  if (!hasNoDuplicateMatch(lead)) return [];
  const hasIngestRun = (stageRuns || []).some((run) => run?.stage_key === 'ingest_dedupe');
  if (hasIngestRun) return [];
  const ingestOption = stageOptions.find(([stageKey]) => stageKey === 'ingest_dedupe');
  if (!ingestOption) return [];
  const [stageKey, label] = ingestOption;
  return [{
    rowKey: `inferred-no-duplicate:${stageKey}`,
    stageKey,
    synthetic: true,
    run: {
      id: null,
      stage_key: stageKey,
      stage_label: label,
      status: 'skipped',
      output_summary_json: JSON.stringify({
        summary: 'No duplicate candidates found. Ready for Triage.',
        duplicate_candidate_count: 0
      })
    }
  }];
}



function hasInformationRequestMonitoring(lead = {}) {
  return Boolean(lead?.information_request_monitoring?.has_request);
}



function shouldFocusOutreachMonitor(lead = {}) {
  const monitoring = lead?.information_request_monitoring || {};
  if (!monitoring.has_request) return false;
  const documentCount = Number(monitoring.document_count_since_request || 0);
  const replyCount = Number(monitoring.inbound_reply_count_since_request || 0);
  const pendingDocumentReviewCount = Number(lead?.pending_document_review_count || 0);
  const responseDetected = monitoring.state === 'received' || documentCount > 0 || replyCount > 0;
  if (responseDetected || pendingDocumentReviewCount > 0) return false;
  const waitStageKey = INFORMATION_REQUEST_STAGE_KEYS.includes(lead.current_stage) || lead.current_stage === 'document_review';
  if (monitoring.non_live_delivery) return waitStageKey;
  return (
    waitStageKey &&
    (lead.status === 'waiting_on_prospect' || monitoring.state === 'polling' || monitoring.state === 'sent')
  );
}



function buildOutreachMonitorRow(lead = {}) {
  const messageId = lead?.information_request_monitoring?.latest_outreach_message_id || 'latest';
  return {
    rowKey: `outreach-response-monitor:${messageId}`,
    stageKey: OUTREACH_RESPONSE_MONITOR_STAGE_KEY,
    run: null,
    synthetic: true,
    syntheticKind: 'outreach_response_monitor'
  };
}



function insertOutreachMonitorRow(rows, lead, effectiveCurrentStageKey) {
  if (!hasInformationRequestMonitoring(lead)) return rows;
  if (rows.some((row) => row.stageKey === OUTREACH_RESPONSE_MONITOR_STAGE_KEY)) return rows;
  const monitorRow = buildOutreachMonitorRow(lead);
  if (effectiveCurrentStageKey === OUTREACH_RESPONSE_MONITOR_STAGE_KEY) {
    rows.push(monitorRow);
    return rows;
  }
  const latestInformationRequestIndex = [...rows]
    .map((row, index) => ({ row, index }))
    .reverse()
    .find((item) => INFORMATION_REQUEST_STAGE_KEYS.includes(item.row.stageKey))?.index;
  if (latestInformationRequestIndex === undefined) {
    rows.push(monitorRow);
    return rows;
  }
  rows.splice(latestInformationRequestIndex + 1, 0, monitorRow);
  return rows;
}



function buildRouteStageRows(stageOptions, stageRuns, effectiveCurrentStageKey, lead) {
  const rows = sortStageRunsChronologically(stageRuns).map((run, index) => ({
    stageKey: run.stage_key,
    run
  }));
  const inferredDedupeRows = getInferredDedupeRows(stageOptions, stageRuns, effectiveCurrentStageKey, lead);
  if (inferredDedupeRows.length) {
    rows.unshift(...inferredDedupeRows);
  }
  const inferredRows = getInferredDocumentBypassRows(stageOptions, stageRuns, effectiveCurrentStageKey, lead);
  const lastRecordedRow = rows[rows.length - 1];
  if (inferredRows.length && lastRecordedRow?.stageKey !== effectiveCurrentStageKey) {
    rows.push(...inferredRows);
  }
  insertOutreachMonitorRow(rows, lead, effectiveCurrentStageKey);
  const lastRow = rows[rows.length - 1];
  if (effectiveCurrentStageKey && lastRow?.stageKey !== effectiveCurrentStageKey) {
    rows.push({
      rowKey: `current-stage:${effectiveCurrentStageKey}`,
      stageKey: effectiveCurrentStageKey,
      run: null,
      synthetic: true
    });
  }
  if (!rows.length && effectiveCurrentStageKey) {
    rows.push({
      stageKey: effectiveCurrentStageKey,
      run: null,
      synthetic: true
    });
  }
  if (rows.length) {
    rows[rows.length - 1] = { ...rows[rows.length - 1], isCurrent: true };
  }
  const stageOccurrences = {};
  return rows.map(

  (row) => {
    const stageKey = row.stageKey || row.run?.stage_key || 'stage';
    const occurrence = stageOccurrences[stageKey] || 0;
    stageOccurrences[stageKey] = occurrence + 1;
    const recordedRunId = row.run?.id;
    return {
      ...row,
      rowKey: recordedRunId ? `stage-run:${recordedRunId}` : row.rowKey || `route-stage:${stageKey}:${occurrence}`,
    };
  });
}



function buildRouteStageItems(stageOptions, stageRuns, effectiveCurrentStageKey, lead) {
  const routeRows = buildRouteStageRows(stageOptions, stageRuns, effectiveCurrentStageKey, lead);
  const currentRouteIndex = routeRows.length - 1;
  return routeRows.map(

  (row, index) => {
    const stageConfig = getStageConfig(stageOptions, row.stageKey, row.run);
    const state = getStageState(row.stageKey, index, currentRouteIndex, row.run, lead);
    const readiness = stageConfig.config?.readiness || {};
    const readinessBlocked = readiness.runnable === false;
    return {
      rowKey: row.rowKey,
      key: row.stageKey,
      label: row.run?.stage_label || stageConfig.label,
      description: row.stageKey === OUTREACH_RESPONSE_MONITOR_STAGE_KEY ?
        getOutreachMonitorDescription(lead) :
        stageConfig.config?.description || STAGE_DESCRIPTIONS[row.stageKey] || 'Configured intake pipeline stage.',
      config: stageConfig.config || {},
      readiness,
      readinessBlocked,
      meta: readinessBlocked ? readiness.reason || 'Stage requirements are not met.' : getStageMeta(row.run, state, row.stageKey, lead),
      state,
      run: row.run,
      isCurrent: Boolean(row.isCurrent),
      synthetic: Boolean(row.synthetic)
    };
  });
}



function buildHumanWorkflowStageItems(workflowRail = {}) {
  return (workflowRail.stages || []).map((stage) => ({
    ...stage,
    rowKey: `human-stage:${stage.key}`,
    config: {},
    readiness: {},
    readinessBlocked: false,
    run: null,
    synthetic: true,
    humanStage: true,
    isCurrent: workflowRail.active_stage_key === stage.key,
    state: stage.state || { label: 'Upcoming', className: 'upcoming' },
    meta: stage.meta || '',
    headerStates: [],
    showRunButton: false,
    showSkipButton: false,
    showAdvancedRerunStage: false,
    canRun: false,
    canSkipStage: false,
    canAdvancedRerunStage: false,
    activeRunCounts: { pipelineCount: 0, stageCount: 0, agentCount: 0 },
  }));
}



function isActualInformationRequestStage(stage) {
  return INFORMATION_REQUEST_STAGE_KEYS.includes(stage?.key) && (!stage.synthetic || stage.isCurrent);
}



function hideUncalledInformationRequestSyntheticRows(stageItems) {
  const hasActualInformationRequest = stageItems.some(isActualInformationRequestStage);
  if (hasActualInformationRequest) return stageItems;
  return stageItems.filter((stage) => !(INFORMATION_REQUEST_STAGE_KEYS.includes(stage.key) && stage.synthetic));
}



function buildStageTimelineEntries(stageItems) {
  const visibleStages = hideUncalledInformationRequestSyntheticRows(stageItems);
  const entries = [];
  let index = 0;
  let displayIndex = 1;

  while (index < visibleStages.length) {
    const stage = visibleStages[index];
    if (isActualInformationRequestStage(stage)) {
      const loopStages = [];
      while (index < visibleStages.length && INFORMATION_REQUEST_LOOP_STAGE_KEYS.has(visibleStages[index].key)) {
        loopStages.push(visibleStages[index]);
        index += 1;
      }
      entries.push({
        type: 'information_request_loop',
        key: `information-request-loop:${displayIndex}`,
        displayIndex,
        stages: loopStages
      });
      displayIndex += 1;
      continue;
    }
    entries.push({
      type: 'stage',
      key: stage.rowKey || stage.key,
      displayIndex,
      stage
    });
    displayIndex += 1;
    index += 1;
  }

  return entries;
}



function formatLoopCount(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}



function getInformationRequestLoopSummary(stages = []) {
  const counts = stages.reduce(

  (current, stage) => {
    if (INFORMATION_REQUEST_STAGE_KEYS.includes(stage?.key)) current.informationRequests += 1;
    else if (stage?.key === OUTREACH_RESPONSE_MONITOR_STAGE_KEY) current.outreachMonitors += 1;
    else if (stage?.key === 'document_review') current.documentReviews += 1;
    else if (stage?.key === 'information_evaluation') current.responseEvaluations += 1;
    return current;
  }, {
    informationRequests: 0,
    outreachMonitors: 0,
    documentReviews: 0,
    responseEvaluations: 0
  });
  return [
  counts.informationRequests ? formatLoopCount(counts.informationRequests, 'info request') : '',
  counts.outreachMonitors ? formatLoopCount(counts.outreachMonitors, 'outreach monitor') : '',
  counts.documentReviews ? formatLoopCount(counts.documentReviews, 'document review') : '',
  counts.responseEvaluations ? formatLoopCount(counts.responseEvaluations, 'response evaluation') : '']
  .filter(Boolean)
  .join(' / ') || formatLoopCount(stages.length, 'stage');
}



function hasNoDuplicateMatch(lead = {}) {
  return lead?.duplicate_review_status === 'not_duplicate' && Number(lead?.duplicate_candidate_count || 0) === 0;
}



function shouldShowSkippedNoDuplicateMatch(stageKey, stageIndex, currentStageIndex, run, lead) {
  if (stageKey !== 'ingest_dedupe' || !hasNoDuplicateMatch(lead)) return false;
  const status = run?.status;
  if (ATTENTION_STATUSES.has(status) || ACTIVE_STATUSES.has(status) || REVIEW_RUN_STATUSES.has(status)) return false;
  if (status === 'skipped' || status === 'completed') return true;
  return !run && currentStageIndex >= 0 && stageIndex < currentStageIndex;
}



function shouldShowSkippedDocumentReviewBypass(stageKey, stageIndex, currentStageIndex, run, lead) {
  if (!INFORMATION_REQUEST_STAGE_KEYS.includes(stageKey) || run) return false;
  if (lead?.current_stage !== 'document_review') return false;
  if (Number(lead?.reviewable_document_count || 0) <= 0) return false;
  return currentStageIndex >= 0 && stageIndex < currentStageIndex;
}



function isTerminalNonProgressLead(lead = {}) {
  return TERMINAL_NON_PROGRESS_STATUSES.has(lead?.status) || TERMINAL_NON_PROGRESS_STATUSES.has(lead?.final_decision);
}



function isUnresolvedDedupeReview(lead = {}) {
  return UNRESOLVED_DUPLICATE_STATUSES.has(lead?.duplicate_review_status);
}



function hasMergedDuplicateResolution(lead = {}) {
  return Number(lead?.merged_duplicate_count || 0) > 0 || Number(lead?.duplicate_status_counts?.merged || 0) > 0;
}



function shouldResumeAfterMergedDedupe(lead = {}) {
  return hasMergedDuplicateResolution(lead) && !isUnresolvedDedupeReview(lead);
}



function getFirstIncompleteStageKey(stageOptions, latestByStage, fallbackStageKey) {
  const ingestIndex = stageOptions.findIndex(([stageKey]) => stageKey === 'ingest_dedupe');
  const startIndex = ingestIndex >= 0 ? ingestIndex : 0;
  for (let index = startIndex; index < stageOptions.length; index += 1) {
    const stageKey = stageOptions[index][0];
    const status = latestByStage[stageKey]?.status;
    if (!COMPLETE_STATUSES.has(status) && status !== 'skipped') return stageKey;
  }
  return fallbackStageKey;
}



function getStageState(stageKey, stageIndex, currentStageIndex, run, lead) {
  if (stageKey === OUTREACH_RESPONSE_MONITOR_STAGE_KEY) {
    const monitoring = lead?.information_request_monitoring || {};
    if (monitoring.non_live_delivery) return { label: monitoring.delivery_mode_label || 'Dev send', className: stageIndex === currentStageIndex ? 'active' : 'completed' };
    if (monitoring.enabled === false) return { label: 'Monitor disabled', className: 'attention' };
    if (stageIndex === currentStageIndex) {
      if (monitoring.state === 'received') return { label: 'Response received', className: 'active' };
      return { label: 'Polling', className: 'active' };
    }
    if (monitoring.state === 'received') return { label: 'Completed', className: 'completed' };
    return { label: monitoring.status_label || 'Outreach sent', className: 'completed' };
  }
  const status = run?.status;
  if (ATTENTION_STATUSES.has(status)) {
    return { label: 'Needs attention', className: 'attention' };
  }
  if (ACTIVE_STATUSES.has(status)) {
    return { label: 'In progress', className: 'active' };
  }
  if (shouldShowSkippedNoDuplicateMatch(stageKey, stageIndex, currentStageIndex, run, lead)) {
    return { label: 'Skipped', className: 'skipped' };
  }
  if (shouldShowSkippedDocumentReviewBypass(stageKey, stageIndex, currentStageIndex, run, lead)) {
    return { label: 'Skipped', className: 'skipped' };
  }
  if (stageKey === run?.stage_key && status === 'skipped') {
    return { label: 'Skipped', className: 'skipped' };
  }
  if (stageKey === run?.stage_key && REVIEW_RUN_STATUSES.has(status)) {
    if (stageKey === 'ingest_dedupe' && !isUnresolvedDedupeReview(lead)) {
      return { label: 'Completed', className: 'completed' };
    }
    return { label: stageKey === 'ingest_dedupe' ? DEDUPE_REVIEW_TITLE : 'Review needed', className: 'attention' };
  }
  if (stageKey === run?.stage_key && COMPLETE_STATUSES.has(status)) {
    return { label: 'Completed', className: 'completed' };
  }
  if (isTerminalNonProgressLead(lead) && !run) {
    if (stageKey === lead.current_stage || (lead.final_decision && stageKey === 'decision')) {
      return { label: 'Completed', className: 'completed' };
    }
    if (currentStageIndex >= 0 && stageIndex < currentStageIndex) {
      return { label: 'Skipped', className: 'skipped' };
    }
  }
  if (stageIndex === currentStageIndex) {
    return { label: 'Current', className: 'current' };
  }
  if (currentStageIndex >= 0 && stageIndex < currentStageIndex) {
    return { label: 'Completed', className: 'completed' };
  }
  return { label: 'Upcoming', className: 'upcoming' };
}



function getOutreachMonitorMeta(lead = {}) {
  const monitoring = lead?.information_request_monitoring || {};
  if (!monitoring.has_request) return 'No outreach request has been sent';
  if (monitoring.non_live_delivery) {
    return [
      monitoring.delivery_mode_label || 'Dev send',
      'not polling',
    ].filter(Boolean).join(' / ');
  }
  const parts = [
    monitoring.poll_interval_label || monitoring.schedule_expression,
    pluralize(Number(monitoring.poll_count_since_request || 0), 'poll'),
    pluralize(Number(monitoring.document_count_since_request || 0), 'document'),
  ].filter(Boolean);
  if (Number(monitoring.inbound_reply_count_since_request || 0) > 0) {
    parts.push(pluralize(Number(monitoring.inbound_reply_count_since_request || 0), 'reply', 'replies'));
  }
  return parts.join(' / ');
}



function getStageMeta(run, state, stageKey = '', lead = {}) {
  if (stageKey === OUTREACH_RESPONSE_MONITOR_STAGE_KEY) {
    return getOutreachMonitorMeta(lead);
  }
  if (!run) {
    if (state?.className === 'skipped' && stageKey === 'ingest_dedupe' && hasNoDuplicateMatch(lead)) return 'Skipped because no duplicate match was found';
    if (state?.className === 'skipped' && INFORMATION_REQUEST_STAGE_KEYS.includes(stageKey) && lead?.current_stage === 'document_review') return 'Skipped because documents need review';
    if (state?.className === 'skipped') return 'Skipped because lead was closed';
    if (state?.className === 'completed' && isTerminalNonProgressLead(lead)) return 'Closed by final decision';
    return state?.className === 'completed' ? 'Completed before recorded run' : 'Not started';
  }
  if (run.error) return run.error;
  const time = getRunTime(run);
  if (state?.className === 'skipped' || run.status === 'skipped') {
    if (stageKey === 'ingest_dedupe' && !time) return 'Skipped because no duplicate match was found';
    return time ? `Skipped ${formatDate(time)}` : 'Skipped';
  }
  if (REVIEW_RUN_STATUSES.has(run.status) && state?.className === 'completed') {
    return time ? `Resolved ${formatDate(time)}` : 'Duplicate review resolved';
  }
  if (REVIEW_RUN_STATUSES.has(run.status) && state?.className === 'attention') {
    const label = stageKey === 'ingest_dedupe' ? DEDUPE_REVIEW_TITLE : 'Review needed';
    return time ? `${label} ${formatDate(time)}` : label;
  }
  if (!time) return titleize(run.status);
  if (run.completed_at) return `Completed ${formatDate(run.completed_at)}`;
  if (run.started_at) return `Started ${formatDate(run.started_at)}`;
  return `Recorded ${formatDate(time)}`;
}



function getValidationErrorSummary(value) {
  const parsed = parseJsonValue(value);
  if (Array.isArray(parsed)) {
    return parsed.slice(0, 2).map(formatStageValue).filter(Boolean).join(' / ');
  }
  if (parsed && typeof parsed === 'object') {
    return formatStageValue(parsed);
  }
  if (typeof parsed === 'string') return parsed;
  return '';
}



function getAgentFailureReason(agentRun) {
  if (!agentRun || !ATTENTION_STATUSES.has(agentRun.status)) return '';
  const label = agentRun.agent_display_name || agentRun.agent_key || 'Agent';
  const reason = agentRun.error || 'Agent failed';
  const validationSummary = getValidationErrorSummary(agentRun.validation_errors_json);
  return [label, reason, validationSummary].filter(Boolean).join(': ');
}



function isInvalidAgentOutputRun(agentRun) {
  if (!agentRun || !ATTENTION_STATUSES.has(agentRun.status)) return false;
  const error = String(agentRun.error || '').toLowerCase();
  return Boolean(agentRun.validation_errors_json) ||
    error.includes('model output was not valid json') ||
    error.includes('model output failed output schema validation');
}



function getLatestInvalidAgentOutputRun(agentRunsForStage = []) {
  return [...(agentRunsForStage || [])]
  .filter(isInvalidAgentOutputRun)
  .sort((left, right) => {
    const timeDiff = getTimestamp(getRunTime(right)) - getTimestamp(getRunTime(left));
    return timeDiff || Number(right.id || 0) - Number(left.id || 0);
  })[0] || null;
}



function getAgentRetryActionKey(agentRun) {
  return `agent-retry:${agentRun?.id || 'unknown'}`;
}



function getStageAttentionReason(stage, agentRunsForStage = []) {
  if (stage?.state?.className !== 'attention') return '';
  if (stage?.run?.error) return stage.run.error;
  const failedAgent = (agentRunsForStage || []).find((agentRun) => ATTENTION_STATUSES.has(agentRun.status));
  return getAgentFailureReason(failedAgent);
}



function normalizeStageHeaderToken(value) {
  return String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}



function getStageHeaderChipLabel(value, maxLength = 38) {
  const text = String(value || '').trim();
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(maxLength - 3, 1)).trim()}...`;
}



function pushStageHeaderState(chips, chip) {
  if (!chip?.label || chips.some((item) => item.label === chip.label)) return;
  chips.push(chip);
}



function isHeaderSignalObject(value) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).some((key) => STAGE_HEADER_SIGNAL_KEYS.has(key))
  );
}



function getStageHeaderOutputObjects(run) {
  const output = parseJsonObject(run?.output_summary_json);
  if (!output || typeof output !== 'object' || Array.isArray(output)) return [];
  const objects = [];
  if (isHeaderSignalObject(output)) objects.push(output);
  Object.values(output).forEach((value) => {
    if (isHeaderSignalObject(value)) objects.push(value);
  });
  return objects;
}



function countOutputArrayItems(objects, keys) {
  return objects.reduce((count, item) => {
    keys.forEach((key) => {
      const value = item?.[key];
      if (Array.isArray(value)) count += value.filter(Boolean).length;
    });
    return count;
  }, 0);
}



function getReviewGateHeaderState(outputObjects) {
  let hasStaffGate = false;
  let hasAttorneyGate = false;
  let hasAutomationStop = false;

  outputObjects.forEach(

  (item) => {
    const gate = normalizeStageHeaderToken(item?.review_gate);
    if (item?.automation_can_continue === false) hasAutomationStop = true;
    if (['staff', 'staff_review', 'human', 'human_review', 'manual_review', 'needs_staff_review'].includes(gate)) {
      hasStaffGate = true;
    }
    if (['attorney', 'attorney_review', 'case_review', 'firm_review'].includes(gate)) {
      hasAttorneyGate = true;
    }
  });

  if (hasAutomationStop) return { label: 'Automation blocked', tone: 'attention' };
  if (hasStaffGate) return { label: 'Staff review', tone: 'attention' };
  if (hasAttorneyGate) return { label: 'Attorney review', tone: 'info' };
  return null;
}



function getDecisionHeaderState(outputObjects) {
  const tokens = [];
  outputObjects.forEach((item) => {
    if (item?.needs_more_info === true) tokens.push('needs_more_info');
    ['decision', 'recommendation', 'requested_information_status', 'classification'].forEach((key) => {
      const token = normalizeStageHeaderToken(item?.[key]);
      if (token) tokens.push(token);
    });
    if (item?.flag_for_human_review === true || item?.human_review_required === true) tokens.push('legacy_review_flag');
  });

  const hasAny =

  (...values) => values.some((value) => tokens.includes(value));
  if (hasAny('human_review', 'staff_review', 'needs_staff_review', 'manual_review')) {
    return { label: 'Advisory review', tone: 'warning' };
  }
  if (hasAny('needs_more_info', 'needs_more_information', 'request_followup', 'request_more_info', 'wait_for_documents')) {
    return { label: 'Needs info', tone: 'warning' };
  }
  if (hasAny('attorney_review', 'case_review', 'firm_review', 'route_to_attorney', 'route_to_firm')) {
    return { label: 'Attorney review', tone: 'info' };
  }
  if (hasAny('not_viable', 'reject', 'rejected', 'archive', 'archived')) {
    return { label: 'Not viable', tone: 'attention' };
  }
  if (hasAny('viable', 'accepted', 'pass', 'complete', 'completed', 'advance', 'satisfied')) {
    return { label: titleize(tokens.find((token) => ['viable', 'accepted', 'pass', 'complete', 'completed', 'advance', 'satisfied'].includes(token))), tone: 'success' };
  }
  if (hasAny('route', 'routed')) {
    return { label: 'Routed', tone: 'success' };
  }
  if (tokens.includes('legacy_review_flag')) {
    return { label: 'Advisory flag', tone: 'warning' };
  }
  return null;
}



function getAgentRunHeaderState(agentRunsForStage = []) {
  const runs = agentRunsForStage || [];
  if (!runs.length) return null;
  const failedCount = runs.filter((run) => ATTENTION_STATUSES.has(run?.status)).length;
  const activeCount = runs.filter((run) => ACTIVE_STATUSES.has(run?.status)).length;
  const reviewCount = runs.filter((run) => REVIEW_RUN_STATUSES.has(run?.status)).length;
  if (failedCount) return { label: pluralize(failedCount, 'agent failed', 'agents failed'), tone: 'attention' };
  if (reviewCount) return { label: pluralize(reviewCount, 'agent review', 'agent reviews'), tone: 'warning' };
  if (activeCount) return { label: pluralize(activeCount, 'agent running', 'agents running'), tone: 'active' };
  if (runs.length > 1) return { label: pluralize(runs.length, 'agent'), tone: 'neutral' };
  return null;
}



function getActiveWorkHeaderState(activeRunCounts = {}) {
  const total = Number(activeRunCounts.pipelineCount || 0) + Number(activeRunCounts.stageCount || 0) + Number(activeRunCounts.agentCount || 0);
  return total > 0 ? { label: pluralize(total, 'active run'), tone: 'active' } : null;
}



function getInformationRequestMonitorHeaderState(stage, lead = {}) {
  if (!INFORMATION_REQUEST_STAGE_KEYS.includes(stage?.key) && stage?.key !== OUTREACH_RESPONSE_MONITOR_STAGE_KEY) return null;
  const monitoring = lead?.information_request_monitoring || {};
  if (!monitoring.has_request) return null;
  const documentCount = Number(monitoring.document_count_since_request || 0);
  const replyCount = Number(monitoring.inbound_reply_count_since_request || 0);
  const pollCount = Number(monitoring.poll_count_since_request || 0);
  if (monitoring.non_live_delivery) {
    if (stage?.key === OUTREACH_RESPONSE_MONITOR_STAGE_KEY) return null;
    return {
      label: monitoring.delivery_mode_label || 'Dev send',
      tone: 'info',
      title: monitoring.delivered_recipient_email ? `Delivered to ${monitoring.delivered_recipient_email}; prospect polling is not active.` : 'Prospect polling is not active for this non-live send.'
    };
  }
  if (documentCount > 0) {
    return {
      label: pluralize(documentCount, 'doc'),
      tone: 'success',
      title: `${pluralize(documentCount, 'document')} received since the latest request`
    };
  }
  if (replyCount > 0) {
    return {
      label: pluralize(replyCount, 'reply', 'replies'),
      tone: 'success',
      title: `${pluralize(replyCount, 'reply', 'replies')} received since the latest request`
    };
  }
  if (monitoring.state === 'polling') {
    return {
      label: pollCount ? `${pollCount} polls` : 'Polling',
      tone: 'active',
      title: monitoring.poll_interval_label || 'Polling for prospect response'
    };
  }
  return {
    label: monitoring.status_label || 'Request sent',
    tone: monitoring.enabled === false ? 'warning' : 'info',
    title: monitoring.poll_interval_label || ''
  };
}



function getMoreInfoHeaderState(outputObjects) {
  const count = countOutputArrayItems(outputObjects, STAGE_HEADER_MORE_INFO_KEYS);
  if (!count) return null;
  return {
    label: 'Needs info',
    tone: 'warning',
    title: `${pluralize(count, 'raw info item')} in agent output`
  };
}



function getRiskFlagHeaderState(outputObjects) {
  const count = countOutputArrayItems(outputObjects, new Set(['risk_flags', 'flag_reasons']));
  if (!count) return null;
  return { label: pluralize(count, 'risk flag'), tone: 'warning' };
}



function getAiProcessingLockReason(lead) {
  if (!lead?.ai_processing_locked) return '';
  return lead.ai_processing_lock_reason || 'AI processing is locked for this lead.';
}



function getStageHeaderStates(stage, lead, agentRunsForStage = [], activeRunCounts = {}) {
  const chips = [];
  const outputObjects = getStageHeaderOutputObjects(stage?.run);

  if (stage?.isCurrent && lead?.ai_processing_locked) {
    pushStageHeaderState(chips, { label: 'AI locked', tone: 'attention', title: getAiProcessingLockReason(lead) });
  }

  if (stage?.isCurrent && lead?.staff_review_required) {
    pushStageHeaderState(chips, { label: 'Staff review required', tone: 'attention', title: lead.staff_review_reason || '' });
  }

  pushStageHeaderState(chips, getActiveWorkHeaderState(activeRunCounts));
  pushStageHeaderState(chips, getAgentRunHeaderState(agentRunsForStage));
  pushStageHeaderState(chips, getReviewGateHeaderState(outputObjects));
  pushStageHeaderState(chips, getDecisionHeaderState(outputObjects));
  pushStageHeaderState(chips, getMoreInfoHeaderState(outputObjects));
  pushStageHeaderState(chips, getRiskFlagHeaderState(outputObjects));
  pushStageHeaderState(chips, getInformationRequestMonitorHeaderState(stage, lead));

  if (stage?.isCurrent && lead?.next_action_label) {
    pushStageHeaderState(chips, {
      label: getStageHeaderChipLabel(`Next: ${lead.next_action_label}`),
      tone: 'info',
      title: lead.next_action_label
    });
  }

  return chips.slice(0, STAGE_HEADER_STATE_LIMIT);
}



function getStageWorkflowActionLabel(stage) {
  switch (stage?.key) {
    case 'ingest_dedupe':
      return 'Run Ingest & Dedupe';
    case 'triage':
      return 'Run Triage';
    case 'analysis':
    case 'specialist_intake_review':
      return 'Run Specialist Intake Review';
    case 'class_action_detection':
      return 'Run Class Action Detection';
    case 'info_gathering':
    case 'information_request':
      return 'Prepare Information Request';
    case 'document_review':
      return 'Review Pending Documents';
    case 'information_evaluation':
      return 'Run Information Evaluation';
    case 'specialist_evaluation':
      return 'Run Specialist Evaluation';
    case 'decision':
      return 'Run Decision Stage';
    case 'final_brief':
      return 'Run Final Brief';
    default:
      return `Run ${stage?.label || 'Stage'}`;
  }
}



function getInformationRequestLoopGuidance({ currentStage, lead, stageOptions, latestByStage }) {
  if (!currentStage || !INFORMATION_REQUEST_LOOP_STAGE_KEYS.has(currentStage.key)) return null;

  if (currentStage.key === OUTREACH_RESPONSE_MONITOR_STAGE_KEY) {
    return null;
  }

  if (INFORMATION_REQUEST_STAGE_KEYS.includes(currentStage.key)) {
    return {
      tone: 'info',
      icon: 'pi pi-send',
      title: 'Send the request, or skip only if the response already exists',
      detail: 'This loop normally starts with an editable email draft. If the client already sent documents or a response note, skip to Document Review instead of sending another request.',
      action: 'skip_information_request',
      actionLabel: 'Skip to Document Review',
      stage: currentStage
    };
  }

  if (currentStage.key === 'document_review') {
    const pendingCount = Number(lead?.pending_document_review_count || 0);
    const reviewableCount = Number(lead?.reviewable_document_count || 0);
    const responseNoteCount = Number(currentStage.readiness?.post_information_request_pipedrive_note_count || 0);
    const nextStage = getNextConfiguredStage(stageOptions, 'document_review');
    const latestDocumentReview = latestByStage?.document_review;
    const completedDocumentReview = latestDocumentReview && COMPLETE_STATUSES.has(latestDocumentReview.status);

    if (currentStage.readinessBlocked) {
      return {
        tone: 'warning',
        icon: 'pi pi-lock',
        title: 'Waiting for documents or a client response',
        detail: currentStage.readiness?.reason || 'Document Review stays locked until at least one uploaded document or post-request Pipedrive note exists.'
      };
    }

    if (pendingCount > 0) {
      return {
        tone: 'info',
        icon: 'pi pi-file',
        title: 'Review the pending documents before the loop advances',
        detail: `${pluralize(pendingCount, 'document')} still ${pendingCount === 1 ? 'needs' : 'need'} review. Each Document Review run only receives unreviewed documents; run it until the pending count reaches zero.`
      };
    }

    if (!nextStage) {
      return {
        tone: 'warning',
        icon: 'pi pi-exclamation-triangle',
        title: 'Document Review has no next stage in this pipeline version',
        detail: 'The lead is pinned to a pipeline version that ends here. Reset or upgrade the lead pipeline before continuing.'
      };
    }

    if (completedDocumentReview) {
      return {
        tone: 'success',
        icon: 'pi pi-check-circle',
        title: 'Document Review is clear',
        detail: `No pending documents remain. The next configured stage is ${nextStage.label}.`
      };
    }

    return {
      tone: 'info',
      icon: 'pi pi-file-check',
      title: 'Review the response package',
      detail: `${pluralize(reviewableCount + responseNoteCount, 'item')} available for review. When all pending documents are reviewed, the loop advances to ${nextStage.label}.`
    };
  }

  if (currentStage.key === 'information_evaluation') {
    return {
      tone: 'info',
      icon: 'pi pi-check-square',
      title: 'Decide whether the request is satisfied',
      detail: 'If the response is incomplete, this stage routes back to Information Request. If it is complete, the lead moves to specialist evaluation.'
    };
  }

  if (currentStage.key === 'specialist_evaluation') {
    return {
      tone: 'info',
      icon: 'pi pi-users',
      title: 'Specialists evaluate after the response is complete',
      detail: 'The selected specialists now decide claim viability. If they need more from the client, the lead loops back to Information Request.'
    };
  }

  return null;
}



function getNextAction(lead, currentRun) {
  if (lead.status === 'engagement_letter_to_be_sent') {
    return {
      title: 'Prepare engagement package',
      detail: 'Finalize the accepted case’s engagement agreement before creating a Matter.'
    };
  }
  if (CLOSED_LEAD_STATUSES.has(lead.status)) {
    return {
      title: titleize(lead.status),
      detail: lead.converted_matter_number ? `Matter ${lead.converted_matter_number}` : 'No active intake action.'
    };
  }
  if (UNRESOLVED_DUPLICATE_STATUSES.has(lead.duplicate_review_status)) {
    return { title: DEDUPE_REVIEW_TITLE, detail: 'Choose Not Duplicate or Merge for every duplicate candidate before advancing.' };
  }
  if (lead.next_action_label) {
    return {
      title: lead.next_action_label,
      detail: lead.next_action_at ? `Due ${formatDate(lead.next_action_at)}` : 'Ready now'
    };
  }
  if (lead.legal_hold) {
    return { title: 'Resolve legal hold', detail: 'Do this before decision or conversion.' };
  }
  if (lead.ai_processing_locked) {
    return { title: 'AI locked', detail: getAiProcessingLockReason(lead) };
  }
  if (lead.staff_review_required) {
    return { title: 'Complete staff review', detail: lead.staff_review_reason || lead.staff_review_role_key || 'Review is required.' };
  }
  if (lead.status === 'ready_for_decision' || lead.current_stage === 'decision') {
    return { title: 'Record a decision', detail: 'Accept, reject, duplicate, or archive.' };
  }
  if (lead.status === 'waiting_on_prospect') {
    return { title: 'Wait for prospect response', detail: 'Follow up if the response is overdue.' };
  }
  switch (lead.current_stage) {
    case 'ingest_dedupe':
      return { title: 'Review new lead', detail: 'Confirm the intake data and duplicate status.' };
    case 'triage':
      if (!currentRun || currentRun.status === 'cleared') {
        return { title: 'Awaiting triage', detail: 'No triage outcome is available yet.' };
      }
      if (['pending', 'queued', 'waiting', 'running', 'processing'].includes(currentRun.status)) {
        return { title: 'Triage in progress', detail: 'Review the outcome when preparation finishes.' };
      }
      if (['failed', 'error'].includes(currentRun.status)) {
        return { title: 'Resolve triage failure', detail: 'Review the failed run before retrying triage.' };
      }
      return { title: 'Review triage outcome', detail: 'Confirm case-type routing and selected specialists.' };
	    case 'analysis':
      case 'specialist_intake_review':
	      return { title: 'Review analysis', detail: 'Review specialist intake findings before asking for more information.' };
    case 'class_action_detection':
      return { title: 'Review class action screening', detail: 'Confirm whether the lead should be tagged as a potential class action.' };
	    case 'info_gathering':
	    case 'information_request':
	      return { title: 'Review outreach', detail: 'Send or update the missing-information request.' };
    case 'document_review':
      return { title: 'Review documents', detail: 'Check uploaded documents and review status.' };
    case 'information_evaluation':
      return { title: 'Evaluate response', detail: 'Confirm the client response satisfies the request.' };
    case 'specialist_evaluation':
      return { title: 'Review specialist evaluation', detail: 'Evaluate claim viability with the completed response.' };
    case 'final_brief':
      return { title: 'Review final brief', detail: 'Prepare the lead for decision.' };
    case 'conversion':
      return { title: 'Confirm conversion', detail: 'Check the engagement-letter or converted matter state.' };
    default:
      return { title: 'Review current stage', detail: 'Continue the intake review.' };
  }
}



function getWorkflowMessage(lead, currentStage, latestActivity) {
  if (currentStage?.key === 'case_review') {
    return 'The AI pipeline is paused while the assigned attorney decides whether to take the case.';
  }
  if (currentStage?.key === 'engagement_package') {
    return 'The attorney accepted the case. Complete the engagement package before creating a Matter.';
  }
  if (lead.legal_hold) {
    return 'This lead is on legal hold. Resolve the hold before moving it toward decision or conversion.';
  }
  if (lead.ai_processing_locked) {
    return getAiProcessingLockReason(lead);
  }
  if (UNRESOLVED_DUPLICATE_STATUSES.has(lead.duplicate_review_status)) {
    return 'Duplicate candidates must be marked Not Duplicate or merged before this lead can advance.';
  }
  if (lead.staff_review_required) {
    return lead.staff_review_reason || 'Staff review is required before this lead can continue.';
  }
  if (lead.status === 'waiting_on_prospect') {
    return 'The information-request stage is waiting on the prospect. Review outreach before following up.';
  }
  if (lead.status === 'engagement_letter_to_be_sent') {
    return 'The attorney accepted the case. Complete the engagement package before creating a Matter.';
  }
  if (lead.status === 'ready_for_decision' || lead.current_stage === 'decision') {
    return 'The review is ready for a final intake decision.';
  }
  if (latestActivity?.time) {
    return `Last activity: ${latestActivity.label} ${formatDate(latestActivity.time)}.`;
  }
  return currentStage?.description || 'Review the current pipeline stage and continue the intake review.';
}




function parseJsonObject(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
}



function titleizeOptional(value) {
  return value ? titleize(value) : '';
}



function formatOptionalDate(value) {
  return value ? formatDate(value) : '';
}



function getStageOutputItems(run, agentRunsForStage = []) {
  const output = parseJsonObject(run?.output_summary_json);
  if (!output) return [];
  const agentRunsByKey = (agentRunsForStage || []).reduce((items, agentRun) => {
    if (agentRun?.agent_key) items[agentRun.agent_key] = agentRun;
    return items;
  }, {});
  return Object.entries(output)
  .filter(([key]) => !STAGE_TIMELINE_HIDDEN_OUTPUT_KEYS.has(key))
  .map(([key, rawValue]) => {
    const agentRun = agentRunsByKey[key] || null;
    return {
      key,
      label: agentRun?.agent_display_name || titleize(key),
      rawValue,
      agentRun,
      agentOutput: Boolean(agentRun),
      structured: rawValue !== null && typeof rawValue === 'object',
      value: formatStageValue(rawValue)
    };
  })
  .filter((item) => item.value || item.structured);
}



function normalizeAgentKey(value) {
  return String(value || '').trim().toLowerCase();
}



function extractSelectedSpecialistKeys(value) {
  const output = typeof value === 'string' ? parseJsonObject(value) : value;
  if (!output || typeof output !== 'object' || Array.isArray(output)) return new Set();
  const direct = output.specialists_to_invoke;
  if (Array.isArray(direct)) {
    return new Set(direct.map(normalizeAgentKey).filter(Boolean));
  }
  for (const nested of Object.values(output)) {
    const selected = extractSelectedSpecialistKeys(nested);
    if (selected.size) return selected;
  }
  return new Set();
}



function getSelectedClaimSpecialistKeys(stageRuns = []) {
  const routingRuns = (stageRuns || [])
  .filter((run) => (
    ['triage', 'claim_routing_assignment'].includes(run?.stage_key) &&
    (COMPLETE_STATUSES.has(run.status) || REVIEW_RUN_STATUSES.has(run.status))
  ))
  .sort((left, right) => {
    const timeDiff = getTimestamp(getRunTime(right)) - getTimestamp(getRunTime(left));
    return timeDiff || (right.id || 0) - (left.id || 0);
  });
  for (const run of routingRuns) {
    const selected = extractSelectedSpecialistKeys(run.output_summary_json);
    if (selected.size) return selected;
  }
  return new Set();
}



function getAgentRole(agent) {
  return String(agent?.agent_role || agent?.role || agent?.orchestration_role || '').trim().toLowerCase();
}



function stageAllowsLegalVerification(stageKey) {
  return CLAIM_SPECIALIST_RUN_STAGE_KEYS.has(normalizeAgentKey(stageKey));
}



function isClaimSpecialistAgentRun(run) {
  const role = String(run?.agent_role || run?.agent_role_snapshot || run?.role || '').trim().toLowerCase();
  const agentKey = normalizeAgentKey(run?.agent_key || run?.agent_key_snapshot);
  return role === 'claim_specialist' || agentKey.endsWith('_specialist');
}



function agentRunAllowsLegalVerification(run) {
  return stageAllowsLegalVerification(run?.stage_key || run?.agent_stage_key || run?.agent_stage_key_snapshot) && isClaimSpecialistAgentRun(run);
}



function isFinalEvaluationStage(stage) {
  if (!stage) return false;
  return (stage.agents || []).some((agent) => (
    FINAL_EVAL_AGENT_ROLES.has(getAgentRole(agent)) ||
    FINAL_EVAL_AGENT_KEYS.has(normalizeAgentKey(agent?.agent_key))
  ));
}



function isRoutedOutClaimSpecialist(stage, agent, selectedClaimSpecialistKeys) {
  return (
    CLAIM_SPECIALIST_RUN_STAGE_KEYS.has(stage?.key) &&
    selectedClaimSpecialistKeys?.size > 0 &&
    getAgentRole(agent) === 'claim_specialist' &&
    !selectedClaimSpecialistKeys.has(normalizeAgentKey(agent?.agent_key))
  );
}



function isSkippedSecondPassSupportAgent(stage, agent) {
  return stage?.key === 'specialist_evaluation' && getAgentRole(agent) !== 'claim_specialist';
}



function getAgentRunsForStage(agentRuns, run) {
  if (!run) return [];
  return agentRuns.filter((agentRun) => agentRun.stage_run_id === run.id);
}



function getLatestAgentRunForAgent(agentRuns, agentKey) {
  const normalizedAgentKey = normalizeAgentKey(agentKey);
  return (agentRuns || [])
  .filter((item) => normalizeAgentKey(item.agent_key) === normalizedAgentKey)
  .sort((left, right) => {
    const timeDiff = getTimestamp(getRunTime(right)) - getTimestamp(getRunTime(left));
    return timeDiff || (right.id || 0) - (left.id || 0);
  })[0] || null;
}



function getAgentRunLabel(agentRun) {
  return getActivityDetail([
  agentRun.agent_display_name || agentRun.agent_key || `Agent #${agentRun.agent_id || '-'}`,
  agentRun.model_tier,
  agentRun.total_tokens ? `${agentRun.total_tokens} tokens` : null]
  );
}



function getSupersededRunIds(stageRuns = []) {
  const latestByStage = {};
  const supersededStageRunIds = new Set();
  const supersededPipelineRunIds = new Set();
  const sortedRuns = [...(stageRuns || [])].sort((left, right) => {
    const rightTime = getTimestamp(getRunTime(right)) || Number(right?.id || 0);
    const leftTime = getTimestamp(getRunTime(left)) || Number(left?.id || 0);
    return rightTime - leftTime || Number(right?.id || 0) - Number(left?.id || 0);
  });

  sortedRuns.forEach(

  (run) => {
    if (!run?.stage_key) return;
    if (!latestByStage[run.stage_key]) {
      latestByStage[run.stage_key] = run;
      return;
    }
    if (ACTIVE_STATUSES.has(run.status)) {
      supersededStageRunIds.add(run.id);
      if (run.pipeline_run_id) supersededPipelineRunIds.add(run.pipeline_run_id);
    }
  });

  return { supersededStageRunIds, supersededPipelineRunIds };
}



function getActiveRunCounts(pipelineRuns = [], stageRuns = [], agentRuns = []) {
  const { supersededStageRunIds, supersededPipelineRunIds } = getSupersededRunIds(stageRuns);
  return {
    pipelineCount: (pipelineRuns || []).filter((item) => ACTIVE_STATUSES.has(item?.status) && !supersededPipelineRunIds.has(item.id)).length,
    stageCount: (stageRuns || []).filter((item) => ACTIVE_STATUSES.has(item?.status) && !supersededStageRunIds.has(item.id)).length,
    agentCount: (agentRuns || []).filter((item) => ACTIVE_STATUSES.has(item?.status) && !supersededStageRunIds.has(item.stage_run_id)).length
  };
}



function getActiveRunCountsForStage(stageKey, pipelineRuns = [], stageRuns = [], agentRuns = []) {
  const { supersededStageRunIds, supersededPipelineRunIds } = getSupersededRunIds(stageRuns);
  const activeStageRuns = (stageRuns || []).filter((item) =>
    item?.stage_key === stageKey &&
    ACTIVE_STATUSES.has(item?.status) &&
    !supersededStageRunIds.has(item.id)
  );
  const activeStageRunIds = new Set(activeStageRuns.map((item) => item.id));
  const activePipelineRunIds = new Set(activeStageRuns.map((item) => item.pipeline_run_id).filter(Boolean));

  return {
    pipelineCount: (pipelineRuns || []).filter((item) =>
      ACTIVE_STATUSES.has(item?.status) &&
      activePipelineRunIds.has(item.id) &&
      !supersededPipelineRunIds.has(item.id)
    ).length,
    stageCount: activeStageRuns.length,
    agentCount: (agentRuns || []).filter((item) =>
      ACTIVE_STATUSES.has(item?.status) &&
      activeStageRunIds.has(item.stage_run_id) &&
      !supersededStageRunIds.has(item.stage_run_id)
    ).length
  };
}



function getActiveRunCountsForStageRun(stageRun, pipelineRuns = [], stageRuns = [], agentRuns = []) {
  if (!stageRun?.id) return { pipelineCount: 0, stageCount: 0, agentCount: 0 };
  const { supersededStageRunIds, supersededPipelineRunIds } = getSupersededRunIds(stageRuns);
  const activeStageRun = ACTIVE_STATUSES.has(stageRun.status) && !supersededStageRunIds.has(stageRun.id);
  const pipelineRunId = stageRun.pipeline_run_id;
  return {
    pipelineCount: (pipelineRuns || []).filter((item) =>
      ACTIVE_STATUSES.has(item?.status) &&
      item.id === pipelineRunId &&
      !supersededPipelineRunIds.has(item.id)
    ).length,
    stageCount: activeStageRun ? 1 : 0,
    agentCount: (agentRuns || []).filter((item) =>
      ACTIVE_STATUSES.has(item?.status) &&
      item.stage_run_id === stageRun.id &&
      !supersededStageRunIds.has(item.stage_run_id)
    ).length
  };
}



function hasActiveStageWork(counts) {
  return Boolean(counts?.pipelineCount || counts?.stageCount || counts?.agentCount);
}



function getRunCountLabel(count, label) {
  if (!count) return null;
  return `${count} ${label}${count === 1 ? '' : 's'}`;
}



function StageRunActivity({ counts, starting = false }) {
  const pipelineCount = counts?.pipelineCount || 0;
  const stageCount = counts?.stageCount || 0;
  const agentCount = counts?.agentCount || 0;
  const total = pipelineCount + stageCount + agentCount;
  if (!total && !starting) return null;
  const parts = [
  getRunCountLabel(pipelineCount, 'pipeline run'),
  getRunCountLabel(stageCount, 'stage run'),
  getRunCountLabel(agentCount, 'agent task')
  ].filter(Boolean);
  return (
    <div className="intake-run-activity" role="status" aria-live="polite">
            <span className="intake-run-activity__icon">
                <AppIcon icon="pi pi-spin pi-spinner" aria-hidden="true" />
            </span>
            <div className="intake-run-activity__body">
                <strong>Working on this stage</strong>
                <span>{parts.length ? 'This may take a moment. Results will appear here as the agent work finishes.' : 'Starting the stage work now.'}</span>
            </div>
            {parts.length > 0 &&
            <div className="intake-run-activity__counts" aria-label="Active work">
                {parts.map((part) => <span key={part}>{part}</span>)}
            </div>}
        </div>);
}



function StageFieldGrid({ items }) {
  const visibleItems = items.filter((item) => item.value !== null && item.value !== undefined && item.value !== '');
  if (!visibleItems.length) return null;

  return (
    <div className="intake-stage-field-grid">
            {visibleItems.map((item) =>
        <div className="intake-stage-field" key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                </div>
      )}
        </div>);

}




function SpecialistOutputSection({ item, index, showLegalVerification = false, onSourceSelect = () => {} }) {
  const [selectedLegalAuthorityFinding, setSelectedLegalAuthorityFinding] = useState(null);
  const run = item.agentRun;
  const output = parseJsonValue(item.rawValue);
  const status = run?.status || 'completed';
  const statusTone = getRunStatusTone(status);
  const itemShowLegalVerification = showLegalVerification && isClaimSpecialistAgentRun(run);
  const meta = getActivityDetail([
    run?.model_tier ? titleize(run.model_tier) : null,
    run?.model || null,
    run?.total_tokens ? `${run.total_tokens} tokens` : null,
    run?.completed_at ? `Completed ${formatDate(run.completed_at)}` : run?.started_at ? `Started ${formatDate(run.started_at)}` : null
  ]);
  return (
    <>
    <details className={`intake-stage-agent-output-section intake-stage-agent-output-section--${statusTone}`}>
      <summary className="intake-stage-agent-output-section__summary">
        <span className="intake-stage-agent-output-section__index">Agent {index + 1}</span>
        <div className="intake-stage-agent-output-section__title">
          <strong>{item.label}</strong>
          {meta && <span>{meta}</span>}
        </div>
        <span className={`intake-stage-agent-output-section__status intake-stage-agent-output-section__status--${statusTone}`}>
          {titleize(status)}
        </span>
        <AppIcon icon="pi pi-chevron-down" aria-hidden="true" />
        <LegalSourceChips
          output={output}
          showLegalVerification={itemShowLegalVerification}
          onSourceSelect={onSourceSelect}
          className="intake-stage-agent-output-section__sources" />
      </summary>
      <div className="intake-stage-agent-output-section__body">
        <FormattedAgentOutput
          value={output}
          title={item.label}
          compact
          showTitle={false}
          showLegalVerification={itemShowLegalVerification}
          showSourceChips={false}
          showAuthorityFindingChips={false} />
        <LegalAuthorityFindingChips
          output={output}
          showLegalVerification={itemShowLegalVerification}
          onFindingSelect={setSelectedLegalAuthorityFinding}
          className="intake-stage-agent-output-section__authority-findings" />
      </div>
    </details>
    <LegalAuthorityFindingDetailModal
      finding={selectedLegalAuthorityFinding}
      onClose={() => setSelectedLegalAuthorityFinding(null)} />
    </>
  );
}



function SpecialistOutputAccordion({ items, showLegalVerification = false, onSourceSelect = () => {} }) {
  return (
    <section className="intake-stage-agent-output-set" aria-label="Agent outputs">
      <div className="intake-stage-agent-output-set__header">
        <span>Agent Outputs</span>
        <strong>{items.length} agents</strong>
      </div>
      <div className="intake-stage-agent-output-set__list">
        {items.map((item, index) =>
          <SpecialistOutputSection
            key={item.key || item.label}
            item={item}
            index={index}
            showLegalVerification={showLegalVerification}
            onSourceSelect={onSourceSelect} />
        )}
      </div>
    </section>
  );
}



function getConfiguredStageAgents(stage, hideAgentKeys = new Set(), selectedClaimSpecialistKeys = new Set()) {
  return (stage.config?.agents || []).filter((agent) =>
    !hideAgentKeys.has(agent.agent_key) &&
    !isSkippedSecondPassSupportAgent(stage, agent) &&
    !isRoutedOutClaimSpecialist(stage, agent, selectedClaimSpecialistKeys)
  );
}



function getAgentProgressLabel(agent, run, outputItem, fallbackKey = '') {
  return (
    agent?.display_name ||
    run?.agent_display_name ||
    outputItem?.label ||
    titleize(agent?.agent_key || run?.agent_key || fallbackKey || 'Agent')
  );
}



function getAgentProgressMeta(run, agent) {
  if (run) {
    return getActivityDetail([
      run.model_tier ? titleize(run.model_tier) : null,
      getModelProviderLabel(run.provider),
      run.model || null,
      run.total_tokens ? `${run.total_tokens} tokens` : null,
      run.completed_at ? `Completed ${formatDate(run.completed_at)}` : run.started_at ? `Started ${formatDate(run.started_at)}` : null
    ]);
  }
  const readiness = agent?.readiness;
  return getModelProfileRunLabel({
    display_name: titleize(readiness?.model_tier),
    provider: readiness?.provider,
    model_id: readiness?.model
  });
}



function getSpecialistAgentProgressItems(stage, agentRunsForStage = [], agentOutputItems = [], hideAgentKeys = new Set(), selectedClaimSpecialistKeys = new Set()) {
  const configuredAgents = getConfiguredStageAgents(stage, hideAgentKeys, selectedClaimSpecialistKeys);
  const outputItemsByKey = (agentOutputItems || []).reduce((items, outputItem) => {
    const outputKey = normalizeAgentKey(outputItem?.key);
    if (outputKey) items[outputKey] = outputItem;
    return items;
  }, {});
  const seen = new Set();
  const items = [];
  const addItem =

  ({ agent = null, run = null, outputItem = null, fallbackKey = '' }) => {
    const agentKey = normalizeAgentKey(agent?.agent_key || run?.agent_key || outputItem?.key || fallbackKey);
    if (!agentKey || seen.has(agentKey)) return;
    seen.add(agentKey);
    const status = outputItem && (!run?.status || ACTIVE_STATUSES.has(run.status)) ?
      'completed' :
      run?.status || (outputItem ? 'completed' : agent?.enabled === false ? 'disabled' : 'ready');
    items.push({
      agentKey,
      label: getAgentProgressLabel(agent, run, outputItem, agentKey),
      meta: getAgentProgressMeta(run, agent),
      status,
      statusTone: getRunStatusTone(status),
      run,
      outputItem
    });
  };

  configuredAgents.forEach((agent) => {
    const agentKey = normalizeAgentKey(agent.agent_key);
    const run = getLatestAgentRunForAgent(agentRunsForStage, agent.agent_key);
    addItem({ agent, run, outputItem: outputItemsByKey[agentKey] });
  });
  (agentRunsForStage || []).forEach((run) => addItem({ run, outputItem: outputItemsByKey[normalizeAgentKey(run.agent_key)] }));
  (agentOutputItems || []).forEach((outputItem) => addItem({ outputItem }));
  return items;
}



function SpecialistAgentProgressCard({ item, index, showLegalVerification = false, onSourceSelect = () => {} }) {
  const output = parseJsonValue(item.outputItem?.rawValue);
  const hasOutput = output && typeof output === 'object';
  const isActive = ACTIVE_STATUSES.has(item.status);
  const itemShowLegalVerification = showLegalVerification && isClaimSpecialistAgentRun(item.run);
  const placeholder = item.run?.error ||
    (isActive ? 'Waiting for this agent result.' :
      COMPLETE_STATUSES.has(item.status) ? 'Completed without structured output.' :
        item.status === 'ready' ? 'Ready to run.' : titleize(item.status));
  return (
    <div className={`intake-stage-agent-output-card intake-stage-agent-output-card--${item.statusTone}`} aria-label={`${item.label} agent progress`}>
      <div className="intake-stage-agent-output-card__header">
        <span className="intake-stage-agent-output-card__index">{index + 1}</span>
        <div className="intake-stage-agent-output-card__title">
          <strong>{item.label}</strong>
          {item.meta && <span>{item.meta}</span>}
        </div>
        <span className={`intake-stage-agent-output-card__status intake-stage-agent-output-card__status--${item.statusTone}`}>
          {isActive && <AppIcon icon="pi pi-spin pi-spinner" aria-hidden="true" />}
          <span>{titleize(item.status)}</span>
        </span>
      </div>
      {hasOutput ?
        <FormattedAgentOutput
          value={output}
          title={item.label}
          compact
          showTitle={false}
          showLegalVerification={itemShowLegalVerification}
          onSourceSelect={onSourceSelect} /> :
        <div className={`intake-stage-agent-output-card__empty intake-stage-agent-output-card__empty--${item.statusTone}`}>
          {isActive && <AppIcon icon="pi pi-spin pi-spinner" aria-hidden="true" />}
          <span>{placeholder}</span>
        </div>}
    </div>
  );
}



function SpecialistAgentProgressPanel({ stage, agentRunsForStage = [], agentOutputItems = [], hideAgentKeys = new Set(), selectedClaimSpecialistKeys = new Set(), showLegalVerification = false }) {
  const [selectedLegalSource, setSelectedLegalSource] = useState(null);
  const items = getSpecialistAgentProgressItems(stage, agentRunsForStage, agentOutputItems, hideAgentKeys, selectedClaimSpecialistKeys);
  const shouldShow = items.length > 0 && (stage.stageRunning || agentRunsForStage.length > 0 || agentOutputItems.length > 0);
  if (!shouldShow) return null;
  const completedCount = items.filter((item) => COMPLETE_STATUSES.has(item.status)).length;
  const activeCount = items.filter((item) => ACTIVE_STATUSES.has(item.status)).length;
  const failedCount = items.filter((item) => ATTENTION_STATUSES.has(item.status)).length;
  const summary = [
    activeCount ? pluralize(activeCount, 'running agent', 'running agents') : null,
    completedCount ? pluralize(completedCount, 'completed agent', 'completed agents') : null,
    failedCount ? pluralize(failedCount, 'failed agent', 'failed agents') : null,
  ].filter(Boolean).join(' / ') || pluralize(items.length, 'agent');
  return (
    <section className="intake-stage-agent-output-set intake-stage-agent-progress" aria-label="Specialist agent progress">
      <div className="intake-stage-agent-output-set__header">
        <span>Agent Progress</span>
        <strong>{summary}</strong>
      </div>
      <div className="intake-stage-agent-output-set__list">
        {items.map((item, index) =>
          <SpecialistAgentProgressCard
            key={item.agentKey}
            item={item}
            index={index}
            showLegalVerification={showLegalVerification}
            onSourceSelect={setSelectedLegalSource} />
        )}
      </div>
      <LegalSourceDetailModal source={selectedLegalSource} onClose={() => setSelectedLegalSource(null)} />
    </section>
  );
}



function StageOutputList({ items, showLegalVerification = false }) {
  const [selectedLegalSource, setSelectedLegalSource] = useState(null);
  const structuredItems = items.filter((item) => item.structured);
  const agentOutputItems = structuredItems.filter((item) => item.agentOutput);
  const nonAgentItems = items.filter((item) => !item.agentOutput);
  const useSeparatedAgentOutputs = agentOutputItems.length > 1;

  if (!items.length) return null;

  if (useSeparatedAgentOutputs) {
    return (
      <div className="intake-stage-output-list intake-stage-output-list--agent-set">
        {nonAgentItems.map((item) =>
          item.structured ?
          <FormattedAgentOutput
            key={item.label}
            value={item.rawValue}
            title={item.label}
            compact
            showLegalVerification={showLegalVerification}
            onSourceSelect={setSelectedLegalSource} /> :
          <div className="intake-stage-output-row" key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        )}
        <SpecialistOutputAccordion
          items={agentOutputItems}
          showLegalVerification={showLegalVerification}
          onSourceSelect={setSelectedLegalSource} />
        <LegalSourceDetailModal source={selectedLegalSource} onClose={() => setSelectedLegalSource(null)} />
      </div>);
  }

  return (
    <div className="intake-stage-output-list">
            {items.map((item) =>
        item.structured ?
        <FormattedAgentOutput
          key={item.label}
          value={item.rawValue}
          title={item.label}
          compact
          showLegalVerification={showLegalVerification}
          onSourceSelect={setSelectedLegalSource} /> :
        <div className="intake-stage-output-row" key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                </div>
      )}
            <LegalSourceDetailModal source={selectedLegalSource} onClose={() => setSelectedLegalSource(null)} />
        </div>);

}



function getStageAgentSummary(agentRunsForStage = []) {
  if (!agentRunsForStage.length) return '';
  if (agentRunsForStage.length <= 3) {
    return agentRunsForStage.map(getAgentRunLabel).join(', ');
  }
  const statusCounts = agentRunsForStage.reduce((counts, run) => {
    const status = titleize(run.status || 'unknown');
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});
  const statusSummary = Object.entries(statusCounts)
  .map(([status, count]) => `${count} ${status.toLowerCase()}`)
  .join(', ');
  return `${agentRunsForStage.length} agents${statusSummary ? ` (${statusSummary})` : ''}`;
}



function StageContextPanel({ title, items = [], outputItems = [], actions = null, children = null, showLegalVerification = false }) {
  return (
    <div className="intake-stage-context">
            <div className="intake-stage-context__header">
                <h3>{title}</h3>
                {actions}
            </div>
            <StageFieldGrid items={items} />
            <StageOutputList items={outputItems} showLegalVerification={showLegalVerification} />
            {children}
        </div>);

}



function DocumentReviewScopeCard({ lead, onRerunFullReview, fullReviewRunning = false, disabled = false, disabledReason = '' }) {
  const pendingCount = Number(lead?.pending_document_review_count || 0);
  const reviewableCount = Number(lead?.reviewable_document_count || 0);
  const reviewedCount = Math.max(reviewableCount - pendingCount, 0);
  const tone = pendingCount > 0 ? 'active' : reviewableCount > 0 ? 'complete' : 'empty';
  const title = pendingCount > 0 ?
    'Next run reviews only unreviewed documents' :
    reviewableCount > 0 ? 'All reviewable documents are reviewed' : 'No reviewable documents yet';
  const detail = pendingCount > 0 ?
    `The agent will receive ${pluralize(pendingCount, 'pending document')}. ${pluralize(reviewedCount, 'reviewed document')} will stay out of this batch.` :
    reviewableCount > 0 ? 'Document Review can advance once the stage is completed.' : 'Upload or sync documents before running Document Review.';

  return (
    <div className={`intake-document-review-scope intake-document-review-scope--${tone}`}>
      <AppIcon icon={pendingCount > 0 ? 'pi pi-filter' : reviewableCount > 0 ? 'pi pi-check-circle' : 'pi pi-folder-open'} aria-hidden="true" />
      <div className="intake-document-review-scope__body">
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
      <div className="intake-document-review-scope__stats" aria-label="Document review counts">
        <span><b>{pendingCount}</b> pending</span>
        <span><b>{reviewedCount}</b> reviewed</span>
        <span><b>{reviewableCount}</b> reviewable</span>
      </div>
      {reviewableCount > 0 && onRerunFullReview &&
        <button
          type="button"
          className="intake-button intake-button--quiet intake-document-review-scope__rerun-button"
          disabled={disabled}
          title={disabledReason || 'Re-review every reviewable document, including documents already marked reviewed.'}
          onClick={onRerunFullReview}>
          <AppIcon icon={fullReviewRunning ? 'pi pi-spin pi-spinner' : 'pi pi-history'} aria-hidden="true" />
          <span>Rerun Full Document Review</span>
        </button>}
    </div>
  );
}



function InvalidAgentOutputNotice({ reason, agentRun, onRetry, runningAction = '', disabled = false, disabledReason = '' }) {
  if (!reason) return null;
  const retrying = agentRun?.id && runningAction === getAgentRetryActionKey(agentRun);
  const label = agentRun?.agent_display_name || agentRun?.agent_key || 'agent';
  return (
    <div className="intake-stage-action-empty intake-stage-action-empty--attention intake-stage-action-empty--with-action">
      <span>{reason}</span>
      {agentRun &&
        <button
          type="button"
          className="intake-button intake-button--quiet"
          disabled={disabled}
          title={disabled ? disabledReason || 'Retry is unavailable right now.' : `Retry ${label} with the same stage context.`}
          aria-label={`Retry ${label} output`}
          onClick={() => onRetry(agentRun)}>
          <AppIcon icon={retrying ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'} aria-hidden="true" />
          <span>{retrying ? 'Retrying' : 'Retry'}</span>
        </button>}
    </div>
  );
}



function StageRunFields({ stage, agentRunsForStage }) {
  const run = stage.run;
  const runStatus = run && REVIEW_RUN_STATUSES.has(run.status) && stage.key === 'ingest_dedupe' ? DEDUPE_REVIEW_TITLE : titleize(run?.status);
  const items = [
  { label: 'Run Status', value: run ? runStatus : 'Not started' },
  { label: 'Completed', value: run?.completed_at ? formatDate(run.completed_at) : '' },
  { label: 'Started', value: !run?.completed_at && run?.started_at ? formatDate(run.started_at) : '' },
  { label: 'Agent', value: getStageAgentSummary(agentRunsForStage) }];

  return items;
}



function StageAgentList({
  stage,
  agentRunsForStage,
  modelProfiles = [],
  manualModelProfileSelections = {},
  onModelProfileChange = () => {},
  onRunStage,
  onRetryAgentRun = () => {},
  onOpenAgentContext,
  onOpenAgentFeedback = () => {},
  runningAction,
  loadingContextRunId,
  loadingFeedbackRunId = null,
  hideAgentKeys = new Set(),
  selectedClaimSpecialistKeys = new Set()
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const agents = getConfiguredStageAgents(stage, hideAgentKeys, selectedClaimSpecialistKeys);
  if (!agents.length && !stage.showAdvancedRerunStage) return null;
  const baseModelOptions = getManualModelProfileOptions(modelProfiles);
  const stageSelectionKey = getStageModelSelectionKey(stage.key);
  const stageModelOptions = [{
    value: '',
    label: 'Pipeline defaults',
    title: 'Pipeline defaults',
    meta: 'Configured per pipeline stage',
    model: 'Use the current pipeline model settings',
  }, ...baseModelOptions];
  const selectedStageModelProfileId = String(manualModelProfileSelections[stageSelectionKey] || '');
  const resolvedStageModelProfileId = stageModelOptions.some((option) => option.value === selectedStageModelProfileId) ? selectedStageModelProfileId : '';
  const agentItems = agents.map(

  (agent) => {
    const run = getLatestAgentRunForAgent(agentRunsForStage, agent.agent_key);
    const readiness = agent.readiness;
    const stageBlocked = stage.readiness?.runnable === false;
    const blocked = stageBlocked || readiness?.runnable === false;
    const blockedReason = stageBlocked ? stage.readiness.reason : readiness?.reason;
    const failureReason = getAgentFailureReason(run);
    const statusText = blocked ? `Unavailable: ${blockedReason || 'Not runnable'}` : failureReason || (run ? titleize(run.status) : agent.enabled === false ? 'Disabled' : 'Ready');
    const statusTone = blocked ? 'danger' : run ? getRunStatusTone(run.status) : agent.enabled === false ? 'muted' : 'neutral';
    const canViewContext = Boolean(run?.id && run?.lead_id);
    const contextLoading = canViewContext && loadingContextRunId === run.id;
    const feedbackLoading = canViewContext && loadingFeedbackRunId === run.id;
    const canRetryInvalidOutput = isInvalidAgentOutputRun(run);
    const retryActionKey = getAgentRetryActionKey(run);
    const retryLoading = canRetryInvalidOutput && runningAction === retryActionKey;
    const retryDisabled = Boolean(runningAction) || blocked || !stage.canAdvancedRerunStage;
    const retryDisabledReason = blocked ? blockedReason : stage.runDisabledReason || 'Retry is unavailable right now.';
    const modelLabel = run ?
      [titleize(run.model_tier), getModelProviderLabel(run.provider), run.model].filter(Boolean).join(' / ') :
      getModelProfileRunLabel({
        display_name: titleize(readiness?.model_tier),
        provider: readiness?.provider,
        model_id: readiness?.model
      });
    return {
      agent,
      run,
      blocked,
      blockedReason,
      failureReason,
      statusText,
      statusTone,
      canViewContext,
      contextLoading,
      feedbackLoading,
      canRetryInvalidOutput,
      retryLoading,
      retryDisabled,
      retryDisabledReason,
      modelLabel,
    };
  });
  return (
            <div className="intake-stage-agent-controls">
            <div className="intake-stage-agent-controls__header">
                <span>Agent Runs</span>
                <small>Review agent status, context, and feedback for this stage.</small>
            </div>
            <div className="intake-stage-agent-controls__list">
	                {agentItems.map((item) => {
        const { agent, run, blocked, blockedReason, failureReason, statusText, statusTone, canViewContext, contextLoading, feedbackLoading, canRetryInvalidOutput, retryLoading, retryDisabled, retryDisabledReason, modelLabel } = item;
        return (
          <div className={`intake-stage-agent-controls__row intake-stage-agent-controls__row--${statusTone}`} key={agent.agent_key}>
                        <div className="intake-stage-agent-controls__identity">
                          <span className="intake-stage-agent-controls__name">{agent.display_name || titleize(agent.agent_key)}</span>
                          <small>{agent.agent_key}</small>
                        </div>
                        <strong className={`intake-stage-agent-controls__status intake-stage-agent-controls__status--${statusTone}`} title={blocked ? blockedReason : failureReason || undefined}>{statusText}</strong>
                        <span className="intake-stage-agent-controls__model-used">{modelLabel || 'Pipeline default model'}</span>
                        <div className="intake-stage-agent-controls__actions">
                          {canViewContext &&
                          <button
                            type="button"
	                            className="intake-button intake-button--quiet intake-stage-agent-controls__context-button"
	                            disabled={contextLoading}
	                            onClick={() => onOpenAgentContext(run)}>
                              <AppIcon icon={contextLoading ? 'pi pi-spin pi-spinner' : 'pi pi-file'} aria-hidden="true" />
                              <span>View Context</span>
	                        </button>}
                          {canRetryInvalidOutput &&
                          <button
                            type="button"
                            className="intake-button intake-button--quiet intake-stage-agent-controls__retry-button"
                            disabled={retryDisabled}
                            title={retryDisabled ? retryDisabledReason : `Retry ${agent.display_name || agent.agent_key} with the same stage context.`}
                            onClick={() => onRetryAgentRun(stage, run)}>
                              <AppIcon icon={retryLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'} aria-hidden="true" />
                              <span>{retryLoading ? 'Retrying' : 'Retry'}</span>
                            </button>}
                          {canViewContext &&
                          <button
                            type="button"
                            className="intake-button intake-button--quiet intake-stage-agent-controls__feedback-button"
                            disabled={feedbackLoading}
                            onClick={() => onOpenAgentFeedback(run)}>
                              <AppIcon icon={feedbackLoading ? 'pi pi-spin pi-spinner' : 'pi pi-comments'} aria-hidden="true" />
                              <span>Feedback</span>
                            </button>}
	                        </div>
	                </div>);
      })}
            </div>
            {stage.showAdvancedRerunStage &&
            <div className={`intake-stage-agent-controls__advanced${advancedOpen ? ' intake-stage-agent-controls__advanced--open' : ''}`}>
              <button
                type="button"
                className="intake-stage-agent-controls__advanced-toggle"
                aria-expanded={advancedOpen}
                onClick={() => setAdvancedOpen((current) => !current)}>
                <AppIcon icon="pi pi-cog" aria-hidden="true" />
                <span>Advanced controls</span>
              </button>
              {advancedOpen && <>
              <p>Rerun the full stage when earlier output should be regenerated. Model overrides apply only to this stage rerun.</p>
                <div className="intake-stage-agent-controls__advanced-row intake-stage-agent-controls__advanced-row--stage">
	                  <span className="intake-stage-agent-controls__name">Full Stage</span>
	                  {stageModelOptions.length > 1 &&
	                    <ModelProfilePicker
	                      id={`stage-model-${stage.key}`}
	                      label="Stage Model"
	                      options={stageModelOptions}
	                      value={resolvedStageModelProfileId}
	                      disabled={Boolean(runningAction) || !stage.canAdvancedRerunStage}
	                      title="Used only when Advanced: Rerun Stage is clicked."
	                      onChange={(nextValue) => onModelProfileChange(stageSelectionKey, nextValue)} />}
                  <button
                    type="button"
                    className="intake-button intake-button--quiet intake-stage-agent-controls__advanced-stage-button"
                    disabled={Boolean(runningAction) || !stage.canAdvancedRerunStage}
                    title={!stage.canAdvancedRerunStage ? stage.runDisabledReason : undefined}
                    onClick={() => {
                      if (!stage.canAdvancedRerunStage) return;
                      onRunStage(stage, { modelProfileId: resolvedStageModelProfileId || null });
                    }}>
                    <AppIcon icon={runningAction === `stage:${stage.key}` || stage.stageRunning ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'} aria-hidden="true" />
                    <span>Advanced: Rerun Stage</span>
                  </button>
                </div>
              </>}
            </div>}
	        </div>);
}



function StageSpecificPanel({ stage, lead, agentRuns, isCurrent, draftControls, onOpenDocuments, onOpenDecision, onRunStage, onRetryAgentRun = () => {}, onContinueInformationEvaluation, onResolveStaffReview, onOpenAgentContext, onOpenAgentFeedback, onRefreshEngagementPackage, runningAction, loadingContextRunId, loadingFeedbackRunId, selectedClaimSpecialistKeys, modelProfiles = [], manualModelProfileSelections = {}, onModelProfileChange = () => {}, showLegalVerification = false, showAgentControls = false }) {
  const agentRunsForStage = getAgentRunsForStage(agentRuns, stage.run);
  const outputItems = getStageOutputItems(stage.run, agentRunsForStage);
  const runItems = StageRunFields({ stage, agentRunsForStage });
  const hideAgentKeys = stage.key === 'ingest_dedupe' && !UNRESOLVED_DUPLICATE_STATUSES.has(lead.duplicate_review_status) ? new Set(['dedupe']) : new Set();
  const isSpecialistProgressStage = CLAIM_SPECIALIST_RUN_STAGE_KEYS.has(stage.key);
  const specialistAgentOutputItems = isSpecialistProgressStage ? outputItems.filter((item) => item.agentOutput) : [];
  const visibleOutputItems = isSpecialistProgressStage ? outputItems.filter((item) => !item.agentOutput) : outputItems;
  const attentionReason = stage.attentionReason || getStageAttentionReason(stage, agentRunsForStage);
  const invalidAgentOutputRun = getLatestInvalidAgentOutputRun(agentRunsForStage);
  const retryDisabled = Boolean(runningAction) || !stage.canAdvancedRerunStage;
  const retryDisabledReason = stage.runDisabledReason || 'Retry is unavailable right now.';
  const attentionNotice = attentionReason ?
  <InvalidAgentOutputNotice
    reason={attentionReason}
    agentRun={invalidAgentOutputRun}
    runningAction={runningAction}
    disabled={retryDisabled}
    disabledReason={retryDisabledReason}
    onRetry={(run) => onRetryAgentRun(stage, run)} /> :
  null;
  const stageShowLegalVerification = showLegalVerification && stageAllowsLegalVerification(stage?.key);
  const specialistProgressPanel = isSpecialistProgressStage ?
    <SpecialistAgentProgressPanel
      stage={stage}
      agentRunsForStage={agentRunsForStage}
      agentOutputItems={specialistAgentOutputItems}
      hideAgentKeys={hideAgentKeys}
      selectedClaimSpecialistKeys={selectedClaimSpecialistKeys}
      showLegalVerification={stageShowLegalVerification} /> :
    null;
  const agentList = !showAgentControls ? null : (
    <StageAgentList
      stage={stage}
	      agentRunsForStage={agentRunsForStage}
	      modelProfiles={modelProfiles}
	      manualModelProfileSelections={manualModelProfileSelections}
      onModelProfileChange={onModelProfileChange}
      onRunStage={onRunStage}
      onRetryAgentRun={onRetryAgentRun}
      onOpenAgentContext={onOpenAgentContext}
      onOpenAgentFeedback={onOpenAgentFeedback}
      runningAction={runningAction}
      loadingContextRunId={loadingContextRunId}
      loadingFeedbackRunId={loadingFeedbackRunId}
      hideAgentKeys={hideAgentKeys}
      selectedClaimSpecialistKeys={selectedClaimSpecialistKeys}
    />
  );
  const informationEvaluationContinueAction = `continue-information-evaluation:${stage.key}`;
  const showInformationEvaluationReviewAction = (
    isCurrent &&
    stage.key === 'information_evaluation' &&
    lead.staff_review_required &&
    onContinueInformationEvaluation
  );
  const informationEvaluationReviewAction = showInformationEvaluationReviewAction ?
    <div className="intake-pipeline-loop-guidance intake-pipeline-loop-guidance--warning">
      <AppIcon icon="pi pi-exclamation-triangle" aria-hidden="true" />
      <div className="intake-pipeline-loop-guidance__body">
        <strong>Staff review is holding this stage</strong>
        <span>{lead.staff_review_reason || 'Review the information evaluation output before continuing without another information request.'}</span>
      </div>
      <button
        type="button"
        className="intake-button intake-button--primary"
        disabled={Boolean(runningAction)}
        onClick={() => onContinueInformationEvaluation(stage)}>
        <AppIcon icon={runningAction === informationEvaluationContinueAction ? 'pi pi-spin pi-spinner' : 'pi pi-forward'} aria-hidden="true" />
        <span>{runningAction === informationEvaluationContinueAction ? 'Continuing' : 'Continue Without Info Request'}</span>
      </button>
    </div> :
    null;
  const staffReviewResolveAction = `resolve-staff-review:${stage.key}`;
  const showStaffReviewResolveAction = (
    isCurrent &&
    lead.staff_review_required &&
    !STAFF_REVIEW_RESOLUTION_BLOCKED_STAGE_KEYS.has(stage.key) &&
    onResolveStaffReview
  );
  const staffReviewResolutionAction = showStaffReviewResolveAction ?
    <div className="intake-pipeline-loop-guidance intake-pipeline-loop-guidance--warning">
      <AppIcon icon="pi pi-exclamation-triangle" aria-hidden="true" />
      <div className="intake-pipeline-loop-guidance__body">
        <strong>Staff review is holding this stage</strong>
        <span>{lead.staff_review_reason || 'Resolve the staff-review hold to continue the remaining pipeline.'}</span>
      </div>
      <button
        type="button"
        className="intake-button intake-button--primary"
        disabled={Boolean(runningAction)}
        onClick={() => onResolveStaffReview(stage)}>
        <AppIcon icon={runningAction === staffReviewResolveAction ? 'pi pi-spin pi-spinner' : 'pi pi-forward'} aria-hidden="true" />
        <span>{runningAction === staffReviewResolveAction ? 'Continuing' : 'Resolve & Continue'}</span>
      </button>
    </div> :
    null;

	  switch (stage.key) {
    case 'case_review': {
      const review = stage.attorney_review || {};
      const presentation = review.case_presentation && typeof review.case_presentation === 'object'
        ? review.case_presentation
        : {};
      return (
        <StageContextPanel
          title="Case Review"
          actions={review.id ? (
            <Link className="intake-button" to={`/intake/reviews/${review.id}`}>
              <AppIcon icon="pi pi-external-link" aria-hidden="true" />
              <span>Open Case Review</span>
            </Link>
          ) : null}
          items={[
            { label: 'Attorney', value: review.attorney_name || review.attorney_email },
            { label: 'Status', value: stage.state?.label },
            { label: 'Sent from', value: presentation.handoff_stage },
            { label: 'Handoff note', value: presentation.handoff_note },
            { label: 'Decision reason', value: review.decision_reason },
          ]} />
      );
    }

	    case 'engagement_package': {
	      const override = stage.engagement_draft?.source_map?._override;
	      if (stage.engagement_draft?.status === 'bypassed') {
	        return (
	          <StageContextPanel
	            title="Engagement Package Bypassed"
	            items={[
	              { label: 'Reason', value: override?.reason },
	              { label: 'By', value: override?.actor_email },
	              { label: 'When', value: formatOptionalDate(override?.overridden_at) },
	            ]}>
	            <div className="intake-stage-action-empty">The accepted lead continued through the administrator override.</div>
	          </StageContextPanel>
	        );
	      }
	      return (
	        <div className="intake-stack">
	          {stage.engagement_draft ? (
	            <EngagementDraftPreview
	              draft={stage.engagement_draft}
	              onRefresh={onRefreshEngagementPackage}
	              documentActionLabel="Edit or replace" />
	          ) : (
	            <div className="intake-stage-action-empty">The engagement draft is not available yet.</div>
	          )}
	        </div>
	      );
	    }

	  case 'ingest_dedupe':
      return (
        <StageContextPanel
          title="Intake Snapshot"
          items={[
          { label: 'Source', value: titleizeOptional(lead.source) },
          { label: 'Subtype', value: titleizeOptional(lead.source_subtype) },
	          { label: 'Pipedrive Deal', value: lead.pipedrive_deal_id },
	          { label: 'Pipedrive Person', value: lead.pipedrive_person_id },
	          { label: 'Created', value: formatOptionalDate(lead.created_at) }].concat(runItems)}
		          outputItems={outputItems} showLegalVerification={stageShowLegalVerification}>{attentionNotice}{agentList}</StageContextPanel>);

	    case 'triage':
	      return (
	        <StageContextPanel
	          title="Triage Result"
	          items={[
	            { label: 'Category', value: titleizeOptional(lead.category) },
	            { label: 'Confidence', value: formatPercent(lead.category_confidence) },
	            { label: 'Client State', value: lead.state_client },
	            { label: 'Defendant State', value: lead.state_defendant },
	            { label: 'Event States', value: lead.state_events },
	            { label: 'Staff Review', value: lead.staff_review_required ? 'Required' : 'Not required' },
	            { label: 'Review Reason', value: lead.staff_review_reason }].concat(runItems)}
	          outputItems={outputItems} showLegalVerification={stageShowLegalVerification}>
		          {attentionNotice}
		          {staffReviewResolutionAction}
		          {agentList}
	        </StageContextPanel>);

    case 'analysis':
    case 'specialist_intake_review':
    case 'class_action_detection':
    case 'specialist_evaluation':
      return (
        <StageContextPanel
          title={stage.key === 'specialist_evaluation' ? 'Specialist Evaluation' : stage.key === 'class_action_detection' ? 'Class Action Detection' : 'AI Review'}
          items={[
          { label: 'Reported Damages', value: lead.damages_reported },
          { label: 'Event Dates', value: lead.event_dates_summary },
          { label: 'Potential Class Action', value: lead.potential_class_action ? 'Yes' : 'No' },
          { label: 'Class Action Reason', value: lead.potential_class_action_reason },
          { label: 'Next Action', value: lead.next_action_label },
          { label: 'Due', value: formatOptionalDate(lead.next_action_at) }].concat(runItems)}
			          outputItems={visibleOutputItems} showLegalVerification={stageShowLegalVerification}>
				                    {attentionNotice}
				                    {!visibleOutputItems.length && !specialistProgressPanel && <div className="intake-stage-action-empty">No analysis output recorded yet.</div>}
                      {specialistProgressPanel}
                      {staffReviewResolutionAction}
                      {agentList}
		                </StageContextPanel>);

		    case 'info_gathering':
		    case 'information_request':
				      if (isCurrent) return <>{attentionNotice}{draftControls}{agentList}</>;
	      return (
	        <StageContextPanel
          title="Outreach"
          items={[
          { label: 'Next Action', value: lead.next_action_label },
	          { label: 'Due', value: formatOptionalDate(lead.next_action_at) },
	          { label: 'Last Inbound', value: formatOptionalDate(lead.last_inbound_contact_at) },
	          { label: 'Follow-up Round', value: lead.follow_up_round }].concat(runItems)}
					          outputItems={outputItems} showLegalVerification={stageShowLegalVerification}>{attentionNotice}{agentList}</StageContextPanel>);

    case OUTREACH_RESPONSE_MONITOR_STAGE_KEY:
      return <InformationRequestMonitoringPanel monitoring={lead.information_request_monitoring} />;

    case 'document_review':
      {
        const pendingCount = Number(lead.pending_document_review_count || 0);
        const reviewableCount = Number(lead.reviewable_document_count || 0);
        const reviewedCount = Math.max(reviewableCount - pendingCount, 0);
      return (
        <StageContextPanel
          title="Document Review"
          actions={isCurrent ?
          <button type="button" className="intake-button" onClick={onOpenDocuments}>
                        <AppIcon icon="pi pi-folder-open" aria-hidden="true" />
                        <span>Open Documents</span>
                    </button> :
          null}
          items={[
	          { label: 'Reported Damages', value: lead.damages_reported },
	          { label: 'Event Dates', value: lead.event_dates_summary },
	          { label: 'Review Scope', value: pendingCount > 0 ? 'Unreviewed documents only' : reviewableCount > 0 ? 'No pending documents' : 'No reviewable documents' },
	          { label: 'Pending Documents', value: pendingCount },
	          { label: 'Reviewed Documents', value: reviewedCount },
	          { label: 'Next Action', value: lead.next_action_label },
	          { label: 'Due', value: formatOptionalDate(lead.next_action_at) }].concat(runItems)}
			          outputItems={outputItems} showLegalVerification={stageShowLegalVerification}>
		          <DocumentReviewScopeCard
	            lead={lead}
	            disabled={Boolean(runningAction) || stage.stageRunning}
	            disabledReason={stage.stageRunning ? stage.runDisabledReason : ''}
	            fullReviewRunning={runningAction === `stage-full-document-review:${stage.key}` || stage.stageRunning}
	            onRerunFullReview={isCurrent ? () => onRunStage(stage, {
	              includeReviewedDocuments: true,
	              actionKey: `stage-full-document-review:${stage.key}`,
	            }) : null}
	          />
          {attentionNotice}
          {agentList}
        </StageContextPanel>);
      }

	    case 'information_evaluation':
	      return (
	        <StageContextPanel
	          title="Information Evaluation"
	          items={[
	          { label: 'Next Action', value: lead.next_action_label },
	          { label: 'Due', value: formatOptionalDate(lead.next_action_at) },
	          { label: 'Last Inbound', value: formatOptionalDate(lead.last_inbound_contact_at) },
	          { label: 'Staff Review', value: lead.staff_review_required ? 'Required' : 'Not required' }].concat(runItems)}
				          outputItems={outputItems} showLegalVerification={stageShowLegalVerification}>{attentionNotice}{informationEvaluationReviewAction}{agentList}</StageContextPanel>);

    case 'final_brief':
      return (
        <StageContextPanel
          title="Final Brief"
          items={[
          { label: 'Owner', value: lead.assigned_owner_email },
	          { label: 'Final Decision', value: titleizeOptional(lead.final_decision) },
	          { label: 'Staff Review', value: lead.staff_review_required ? 'Required' : 'Complete' },
		          { label: 'Review Reason', value: lead.staff_review_reason }].concat(runItems)}
			          outputItems={outputItems} showLegalVerification={stageShowLegalVerification}>{attentionNotice}{staffReviewResolutionAction}{agentList}</StageContextPanel>);

    case 'decision':
      return (
        <StageContextPanel
          title="Decision"
          items={[
	          { label: 'Status', value: titleizeOptional(lead.status) },
	          { label: 'Final Decision', value: titleizeOptional(lead.final_decision) },
		          { label: 'Matter', value: lead.converted_matter_number }].concat(runItems)}
			          outputItems={outputItems} showLegalVerification={stageShowLegalVerification}>{attentionNotice}{agentList}</StageContextPanel>);

    case 'conversion':
      return (
        <StageContextPanel
          title="Conversion"
          items={[
          { label: 'Matter', value: lead.converted_matter_number },
          { label: 'Client ID', value: lead.converted_client_id },
          { label: 'Status', value: titleizeOptional(lead.status) }].concat(runItems)}
          outputItems={outputItems} showLegalVerification={stageShowLegalVerification}>
                    {attentionNotice}
                    {lead.converted_matter_number && <Link className="intake-stage-link" to={`/case_details/${lead.converted_matter_number}`}>Open matter</Link>}
                </StageContextPanel>);

	    default:
	      return (
	        <StageContextPanel
	          title={stage.label}
		          items={runItems}
			          outputItems={outputItems} showLegalVerification={stageShowLegalVerification}>{attentionNotice}{staffReviewResolutionAction}{agentList}</StageContextPanel>);
  }
}



function PipelineTab({
  lead,
  pipelineRuns,
  stageRuns,
  agentRuns,
  finalRecommendations = [],
	  aiSpending = null,
	  pipelineVersion,
	  latestPipelineVersion,
	  pipelineStages,
	  workflowRail = {},
	  modelProfiles = [],
	  canUpgradePipeline,
  pipelineLoaded = false,
  loading = false,
  error = '',
  engagementLetterHandoff = false,
	  onLeadUpdated,
	  onRefreshLead,
  onRefreshPipeline,
  onOpenDocuments,
  onOpenDecision,
	  readOnly = false,
	}) {
  const showToast = useContext(ToastContext);
  const {
    detail: agentContextDetail,
    loadingRunId: loadingContextRunId,
    openAgentRunDetail: openAgentContextDetail,
    closeAgentRunDetail: closeAgentContextDetail
  } = useAgentRunDetailModal();
  const {
    target: agentFeedbackTarget,
    feedbackItems: agentFeedbackItems,
    loading: agentFeedbackLoading,
    saving: agentFeedbackSaving,
    loadingRunId: loadingFeedbackRunId,
    openAgentFeedback,
    closeAgentFeedback,
    submitAgentFeedback,
    deactivateAgentFeedback
  } = useAgentFeedbackModal();
  const [rows, setRows] = useState([]);
  const [loadingOutreach, setLoadingOutreach] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [sendingTarget, setSendingTarget] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(getDefaultOutreachForm(lead));
	  const [deliveryPolicy, setDeliveryPolicy] = useState(null);
	  const [runningAction, setRunningAction] = useState('');
  const [manualModelProfileSelections, setManualModelProfileSelections] = useState({});
  const [showSpending, setShowSpending] = useState(readSpendingViewPreference);
  const [showLegalVerification, setShowLegalVerification] = useState(readLegalVerificationViewPreference);
  const [showAgentControls, setShowAgentControls] = useState(readAgentControlsViewPreference);
  const [showRuntimeDetail, setShowRuntimeDetail] = useState(readRuntimeDetailViewPreference);
  const refreshInFlightRef = useRef(false);
  const outreachLeadIdRef = useRef(null);
  const selectedIdRef = useRef(null);
  const runRefreshKey = [
    ...(stageRuns || []).map((run) => `s:${run.id}:${run.status}:${run.updated_at || run.completed_at || run.started_at || ''}`),
    ...(agentRuns || []).map((run) => `a:${run.id}:${run.status}:${run.updated_at || run.completed_at || run.started_at || ''}`)
  ].join('|');

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const latestByStage = getLatestByStage(stageRuns);
  const stageOptions = getConfiguredStageOptions(pipelineStages);
  const selectedClaimSpecialistKeys = getSelectedClaimSpecialistKeys(stageRuns);
  const hasUnresolvedDuplicates = UNRESOLVED_DUPLICATE_STATUSES.has(lead.duplicate_review_status);
  const duplicateBlockedStageKey = hasUnresolvedDuplicates ? 'ingest_dedupe' : null;
  const monitorFocusedStageKey = !duplicateBlockedStageKey && shouldFocusOutreachMonitor(lead) ? OUTREACH_RESPONSE_MONITOR_STAGE_KEY : null;
  const rawEffectiveCurrentStageKey = duplicateBlockedStageKey || monitorFocusedStageKey || lead.current_stage;
  const effectiveCurrentStageKey = shouldResumeAfterMergedDedupe(lead) ?
    getFirstIncompleteStageKey(stageOptions, latestByStage, rawEffectiveCurrentStageKey) :
    rawEffectiveCurrentStageKey;
	  const rawBaseStageItems = buildRouteStageItems(stageOptions, stageRuns, effectiveCurrentStageKey, lead);
	  const aiPaused = Boolean(workflowRail.ai_paused);
	  const aiPauseReason = workflowRail.ai_pause_reason || 'AI stages are paused for a human workflow stage.';
	  const baseStageItems = aiPaused ? rawBaseStageItems.map((stage) => (
	    stage.isCurrent ? {
	      ...stage,
	      isCurrent: false,
	      state: stage.run && COMPLETE_STATUSES.has(stage.run.status)
	        ? { label: 'Completed', className: 'completed' }
	        : { label: 'Paused', className: 'upcoming' },
	      meta: stage.run && COMPLETE_STATUSES.has(stage.run.status) ? stage.meta : aiPauseReason,
	    } : stage
	  )) : rawBaseStageItems;
	  const currentRouteIndex = baseStageItems.findIndex((stage) => stage.isCurrent);
	  const leadClosed = CLOSED_LEAD_STATUSES.has(lead.status);
  const aiProcessingLockReason = getAiProcessingLockReason(lead);
  const aiProcessingLocked = Boolean(aiProcessingLockReason);
  const getStageActiveRunCounts =

  (stage) => stage.run?.id ?
    getActiveRunCountsForStageRun(stage.run, pipelineRuns, stageRuns, agentRuns) :
    getActiveRunCountsForStage(stage.key, pipelineRuns, stageRuns, agentRuns);
  const activeRunCounts = getActiveRunCounts(pipelineRuns, stageRuns, agentRuns);
  const hasActivePipelineWork = Boolean(
    activeRunCounts.pipelineCount ||
    activeRunCounts.stageCount ||
    activeRunCounts.agentCount ||
    lead?.has_active_pipeline_work ||
    Number(lead?.active_pipeline_work_count || 0)
  );
  const stageItems = baseStageItems.map(

  (stage, index) => {
    const readinessBlocked = stage.readiness?.runnable === false;
    const isOutreachMonitorStage = stage.key === OUTREACH_RESPONSE_MONITOR_STAGE_KEY;
    const isCurrentRouteStage = index === currentRouteIndex;
    const isRouteStageAtOrBeforeCurrent = currentRouteIndex >= 0 && index <= currentRouteIndex;
    const stageActiveRunCounts = getStageActiveRunCounts(stage);
    const agentRunsForStage = getAgentRunsForStage(agentRuns, stage.run);
    const stageRunning = hasActiveStageWork(stageActiveRunCounts);
	    const runDisabledReason = aiPaused ?
	      aiPauseReason :
	      aiProcessingLocked ?
      aiProcessingLockReason :
      stageRunning ?
      'A run is already in progress for this stage.' :
      readinessBlocked ? stage.readiness?.reason || 'Stage requirements are not met.' :
      hasActivePipelineWork ? 'Other pipeline work is running for this lead.' : '';
	    const showSkipButton = !readOnly && !aiPaused && !leadClosed &&
      isCurrentRouteStage &&
      MANUAL_SKIPPABLE_STAGE_KEYS.has(stage.key) &&
      !INFORMATION_REQUEST_STAGE_KEYS.includes(stage.key);
    const skipDisabledReason = stageRunning ?
      'A run is already in progress for this stage.' :
      hasActivePipelineWork ? 'Other pipeline work is running for this lead.' : '';
	    const showRunButton = !readOnly && !aiPaused && !leadClosed && !readinessBlocked && isCurrentRouteStage;
	    const showAdvancedRerunStage = !readOnly && !aiPaused && !leadClosed && isRouteStageAtOrBeforeCurrent && !isOutreachMonitorStage;
	    const canRun = showRunButton && !aiPaused && !aiProcessingLocked && !stageRunning && !isOutreachMonitorStage;
    const canSkipStage = showSkipButton && !stageRunning && !hasActivePipelineWork;
    const canAdvancedRerunStage = showAdvancedRerunStage && !aiProcessingLocked && !readinessBlocked && !stageRunning && !hasActivePipelineWork;
    const runActionLabel = getStageWorkflowActionLabel(stage);
    const attentionReason = getStageAttentionReason(stage, agentRunsForStage);
    return {
      ...stage,
      activeRunCounts: stageActiveRunCounts,
      canRun,
      canSkipStage,
      canAdvancedRerunStage,
      closed: leadClosed,
      aiProcessingLocked,
      readinessBlocked,
      runDisabledReason,
      showAdvancedRerunStage,
      showSkipButton,
      showRunButton: showRunButton && !isOutreachMonitorStage,
      skipDisabledReason,
      stageRunning,
      headerStates: getStageHeaderStates(stage, lead, agentRunsForStage, stageActiveRunCounts),
      runActionLabel,
      attentionReason,
      meta: attentionReason ? 'Failure details below' : stage.meta
    };
  });
	  const humanWorkflowStageItems = buildHumanWorkflowStageItems(workflowRail);
	  const workflowStageItems = [
	    ...stageItems.filter((stage) => stage.key !== 'conversion'),
	    ...humanWorkflowStageItems,
	  ];
  const workflowTimelineEntries = buildStageTimelineEntries(workflowStageItems);
  const workflowTimelineStageCount = workflowTimelineEntries.reduce((count, entry) => {
    if (entry.type === 'information_request_loop') return count + entry.stages.length;
    return count + 1;
  }, 0);
	  const currentStage = workflowStageItems.find((stage) => stage.isCurrent);
  const currentStageRowKey = currentStage?.rowKey || null;
  const activityItems = buildActivityItems(pipelineRuns, stageRuns, agentRuns);
  const latestActivity = activityItems[0];
  const nextAction = getNextAction(lead, latestByStage[lead.current_stage]);
  const latestFinalRecommendation = getLatestFinalRecommendation(finalRecommendations);
  const decisionBrief = buildDecisionBrief(nextAction, latestFinalRecommendation);
  const workflowMessage = getWorkflowMessage(lead, currentStage, latestActivity);
  const selectedRow = rows.find((row) => row.id === selectedId);
  const isNewDraft = selectedId === 'new';
  const editorActive = isNewDraft || selectedRow;
  const editorLocked = selectedRow && !EDITABLE_OUTREACH_STATUSES.has(selectedRow.status);
  const requiredSendFieldsPresent = Boolean(form.recipient_email && form.subject && form.body);
  const deliveryNotice = getOutreachDeliveryNotice(deliveryPolicy, form.recipient_email);
  const devDeliveryTarget = deliveryPolicy?.redirect_email || (
    String(deliveryPolicy?.mode || '').toLowerCase() === 'redirect' ? deliveryPolicy?.delivered_recipient_email : ''
  );
  const canSendBase = Boolean(editorActive) &&
    form.message_type === 'email' &&
    requiredSendFieldsPresent &&
    !editorLocked &&
    !SEND_BLOCKED_OUTREACH_STATUSES.has(selectedRow?.status) &&
    !saving &&
    !sendingId;
  const canSendToDev = canSendBase && Boolean(devDeliveryTarget);
  const canSendToActual = canSendBase;
  const draftRows = rows.filter((row) => row.message_type === 'email' && EDITABLE_OUTREACH_STATUSES.has(row.status));
  const showEmptyDraftPrompt = !loadingOutreach && !draftRows.length && !editorActive;
  const draftGridClassName = `intake-pipeline-control__grid${!loadingOutreach && !draftRows.length && editorActive ? ' intake-pipeline-control__grid--single' : ''}`;
  const stageActionItems = buildStageActionItems(rows, deliveryPolicy);
  const informationRequestLoopGuidance = getInformationRequestLoopGuidance({ currentStage, lead, stageOptions, latestByStage });
  const overviewClassName = `intake-pipeline-overview${showSpending ? ' intake-pipeline-overview--spending' : ''}`;
	  const activeRunCountsByStage = workflowStageItems.reduce((countsByStage, stage) => {
    countsByStage[stage.rowKey] = stage.activeRunCounts;
    return countsByStage;
  }, {});

	  const openAgentContext =

	  (run) => openAgentContextDetail(run?.lead_id, run?.id);
	  const setManualModelProfileSelection =

	  (selectionKey, modelProfileId) => {
	    setManualModelProfileSelections((current) => ({
	      ...current,
	      [selectionKey]: modelProfileId
	    }));
	  };
	  const modelProfileLookup =

	  (modelProfileId) => getModelProfileById(modelProfiles, modelProfileId);
  const handleSpendingToggle =

  (event) => {
    const enabled = event.target.checked;
    setShowSpending(enabled);
    writeSpendingViewPreference(enabled);
  };
  const handleLegalVerificationToggle =

  (event) => {
    const enabled = event.target.checked;
    setShowLegalVerification(enabled);
    writeLegalVerificationViewPreference(enabled);
  };
  const handleAgentControlsToggle =

  (event) => {
    const enabled = event.target.checked;
    setShowAgentControls(enabled);
    writeAgentControlsViewPreference(enabled);
  };
  const handleRuntimeDetailToggle =

  (event) => {
    const enabled = event.target.checked;
    setShowRuntimeDetail(enabled);
    writeRuntimeDetailViewPreference(enabled);
  };

  const refreshAfterRun =

  async () => {
    await Promise.all([
      onRefreshLead ? onRefreshLead() : Promise.resolve(),
      onRefreshPipeline ? onRefreshPipeline() : Promise.resolve()
    ]);
  };

  const runRemainingActionKey = 'pipeline:remaining';
  const currentStageRunsAsFinalEvaluation = currentStage?.key === 'decision' && isFinalEvaluationStage(currentStage);
  const finalEvaluationAlreadyCompleted = (
    currentStageRunsAsFinalEvaluation &&
    COMPLETE_STATUSES.has(latestByStage[currentStage.key]?.status) &&
    ['ready_for_decision', 'needs_staff_review'].includes(lead.status)
  );
	  const runRemainingDisabledReason = leadClosed ?
	    'Lead is closed.' :
	    aiPaused ?
	      aiPauseReason :
	    aiProcessingLocked ?
      aiProcessingLockReason :
    hasActivePipelineWork ?
      'Pipeline stages are already running.' :
      !currentStage ?
        'No current pipeline stage is available.' :
        finalEvaluationAlreadyCompleted ?
          'Final evaluation is complete; pipeline is ready for human case review.' :
        RUN_ALL_STOP_STAGE_KEYS.has(currentStage.key) && !currentStageRunsAsFinalEvaluation ?
          'Pipeline is waiting for a manual decision or conversion step.' :
          currentStage.readinessBlocked ?
            currentStage.readiness?.reason || 'Resolve the current stage blocker first.' :
            '';
  const canRunRemainingPipeline = !runRemainingDisabledReason;
  const runRemainingButtonRunning = runningAction === runRemainingActionKey || hasActivePipelineWork;
  const runRemainingButtonLabel = getRunPipelineActionLabel({
    currentStageKey: currentStage?.key || lead.current_stage,
    pipelineStages: stageOptions,
    running: runRemainingButtonRunning
  });
  const runRemainingButtonTitle = getRunPipelineActionTitle({
    currentStageKey: currentStage?.key || lead.current_stage,
    pipelineStages: stageOptions,
    disabledReason: runRemainingDisabledReason
  });

  useEffect(

  () => {
    if (!hasActivePipelineWork || (!onRefreshLead && !onRefreshPipeline)) return undefined;

    const refreshActiveRuns =

    () => {
      if (refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;
      Promise.all([
        onRefreshLead ? onRefreshLead() : Promise.resolve(),
        onRefreshPipeline ? onRefreshPipeline() : Promise.resolve()
      ]).finally(() => {
        refreshInFlightRef.current = false;
      });
    };

    const intervalId = window.setInterval(refreshActiveRuns, ACTIVE_RUN_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [hasActivePipelineWork, onRefreshLead, onRefreshPipeline]);

  const getRunResponseStatus =

  (response, fallbackKey) =>
    response?.data?.run_result?.status || response?.data?.[fallbackKey]?.status || '';

  const getRunResponseMessage =

  (response, fallback) =>
    response?.data?.run_result?.message || response?.data?.stage_run?.error || response?.data?.agent_run?.error || fallback;

  const runStage =

  async (stage, options = {}) => {
    if (!stage?.key || stage.stageRunning) return;
    const modelProfileId = options.modelProfileId || null;
    const modelProfile = modelProfileLookup(modelProfileId);
    const modelSuffix = modelProfile ? ` using ${getModelProfileRunLabel(modelProfile)}` : '';
    const fullDocumentReview = Boolean(options.includeReviewedDocuments);
    const reason = fullDocumentReview ?
      `Manual full document review rerun: ${stage.label || stage.key}${modelSuffix}` :
      `Manual stage run: ${stage.label || stage.key}${modelSuffix}`;
    const actionKey = options.actionKey || `stage:${stage.key}`;
    setRunningAction(actionKey);
    try {
      const payload = { stage_key: stage.key, reason };
      if (modelProfileId) payload.model_profile_id = Number(modelProfileId);
      if (showLegalVerification && CLAIM_SPECIALIST_RUN_STAGE_KEYS.has(stage.key)) payload.legal_verification_enabled = true;
      if (fullDocumentReview) {
        payload.include_reviewed_documents = true;
        payload.document_review_scope = 'all_reviewable_documents';
      }
      const response = await runIntakeStage(lead.id, payload);
      if (response?.data?.lead && onLeadUpdated) onLeadUpdated(response.data.lead, response.data);
      await refreshAfterRun();
      const status = getRunResponseStatus(response, 'stage_run');
      if (FAILED_RUN_STATUSES.has(status)) {
        showToast({ severity: 'error', summary: 'Failed', detail: getRunResponseMessage(response, `${stage.label || 'Stage'} run failed`) });
      } else if (RUNNING_RUN_STATUSES.has(status)) {
        showToast({ severity: 'info', summary: 'Running', detail: getRunResponseMessage(response, `${stage.label || 'Stage'} run is running`) });
      } else if (QUEUED_RUN_STATUSES.has(status)) {
        showToast({ severity: 'info', summary: 'Queued', detail: getRunResponseMessage(response, `${stage.label || 'Stage'} run queued`) });
      } else if (NO_ACTION_RUN_STATUSES.has(status)) {
        showToast({ severity: 'warn', summary: 'No Action', detail: getRunResponseMessage(response, `${stage.label || 'Stage'} has no runnable work configured`) });
      } else if (REVIEW_RUN_STATUSES.has(status)) {
        showToast({ severity: 'warn', summary: stage.key === 'ingest_dedupe' ? DEDUPE_REVIEW_TITLE : 'Review Needed', detail: getRunResponseMessage(response, `${stage.label || 'Stage'} needs review before advancing`) });
      } else {
        showToast({ severity: 'success', summary: 'Completed', detail: getRunResponseMessage(response, `${stage.label || 'Stage'} run completed`) });
      }
    } catch (error) {
      console.error('Failed to run intake stage:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to run stage' });
    } finally {
      setRunningAction('');
    }
  };

  const retryAgentRun =

  async (stage, run) => {
    if (!stage?.key || !run?.agent_key || !isInvalidAgentOutputRun(run)) return;
    const actionKey = getAgentRetryActionKey(run);
    const agentLabel = run.agent_display_name || run.agent_key || 'Agent';
    setRunningAction(actionKey);
    try {
      const payload = {
        stage_key: stage.key,
        agent_key: run.agent_key,
        reason: `Retry invalid agent output: ${agentLabel}`,
      };
      if (run.resolved_model_profile_id) payload.model_profile_id = Number(run.resolved_model_profile_id);
      if (showLegalVerification && agentRunAllowsLegalVerification(run)) payload.legal_verification_enabled = true;
      const response = await runIntakeAgent(lead.id, payload);
      if (response?.data?.lead && onLeadUpdated) onLeadUpdated(response.data.lead, response.data);
      await refreshAfterRun();
      const status = getRunResponseStatus(response, 'agent_run');
      if (FAILED_RUN_STATUSES.has(status)) {
        showToast({ severity: 'error', summary: 'Retry Failed', detail: getRunResponseMessage(response, `${agentLabel} retry failed`) });
      } else if (RUNNING_RUN_STATUSES.has(status)) {
        showToast({ severity: 'info', summary: 'Retry Running', detail: getRunResponseMessage(response, `${agentLabel} retry is running`) });
      } else if (QUEUED_RUN_STATUSES.has(status)) {
        showToast({ severity: 'info', summary: 'Retry Queued', detail: getRunResponseMessage(response, `${agentLabel} retry queued`) });
      } else {
        showToast({ severity: 'success', summary: 'Retry Requested', detail: getRunResponseMessage(response, `${agentLabel} retry requested`) });
      }
    } catch (error) {
      console.error('Failed to retry intake agent run:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to retry agent output' });
    } finally {
      setRunningAction('');
    }
  };

  const latestRunAllResult =

  (result) => {
    let latest = result || {};
    let guard = 0;
    while (latest?.run_result?.manual_pipeline_continuation && guard < 20) {
      latest = latest.run_result.manual_pipeline_continuation;
      guard += 1;
    }
    return latest || result || {};
  };

  const runRemainingPipeline =

  async () => {
    if (!canRunRemainingPipeline || runningAction || hasActivePipelineWork) return;
    setRunningAction(runRemainingActionKey);
    try {
      const payload = { reason: 'Manual run remaining pipeline' };
      if (showLegalVerification) payload.legal_verification_enabled = true;
      const response = await runIntakePipeline(lead.id, payload);
      if (response?.data?.lead && onLeadUpdated) onLeadUpdated(response.data.lead, response.data);
      await refreshAfterRun();
      const result = latestRunAllResult(response?.data?.run_all_result);
      const detail = result.message || 'Run remaining pipeline requested.';
      if (result.status === 'blocked') {
        showToast({ severity: 'warn', summary: 'Pipeline Paused', detail });
      } else if (result.status === 'completed' || result.status === 'stopped') {
        showToast({ severity: 'success', summary: 'Pipeline Stopped', detail });
      } else if (result.status === 'waiting') {
        showToast({ severity: 'info', summary: 'Pipeline Armed', detail });
      } else {
        showToast({ severity: 'info', summary: 'Pipeline Running', detail });
      }
    } catch (error) {
      console.error('Failed to run remaining intake pipeline:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to run remaining pipeline' });
    } finally {
      setRunningAction('');
    }
  };

  const skipStage =

  async (stage) => {
    if (!stage?.key) return;
    const reason = `Manual stage skip: ${stage.label || stage.key}`;
    const actionKey = `skip-stage:${stage.key}`;
    setRunningAction(actionKey);
    try {
      const response = await skipIntakeStage(lead.id, { stage_key: stage.key, reason });
      if (response?.data?.lead && onLeadUpdated) onLeadUpdated(response.data.lead, response.data);
      await refreshAfterRun();
      showToast({ severity: 'success', summary: 'Skipped', detail: getRunResponseMessage(response, `${stage.label || 'Stage'} skipped`) });
    } catch (error) {
      console.error('Failed to skip intake stage:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to skip stage' });
    } finally {
      setRunningAction('');
    }
  };

  const continueInformationEvaluation =

  async (stage) => {
    if (!stage?.key) return;
    const actionKey = `continue-information-evaluation:${stage.key}`;
    const reason = 'Staff reviewed the Information Evaluation hold and approved continuing without another information request.';
    setRunningAction(actionKey);
    try {
      const response = await continueInformationEvaluationReview(lead.id, { reason });
      if (response?.data?.lead && onLeadUpdated) onLeadUpdated(response.data.lead, response.data);
      await refreshAfterRun();
      const nextLabel = response?.data?.next_stage?.label || titleize(response?.data?.lead?.current_stage || 'next stage');
      showToast({ severity: 'success', summary: 'Continued', detail: `Advanced to ${nextLabel}` });
    } catch (error) {
      console.error('Failed to continue Information Evaluation:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to continue information review' });
    } finally {
      setRunningAction('');
    }
  };

  const resolveStaffReview =

  async (stage) => {
    if (!stage?.key) return;
    const actionKey = `resolve-staff-review:${stage.key}`;
    const reason = `Staff reviewed the ${stage.label || stage.key} hold and approved continuing the remaining pipeline.`;
    setRunningAction(actionKey);
    try {
      const response = await resolveIntakeStaffReview(lead.id, { reason, continue_pipeline: true });
      if (response?.data?.lead && onLeadUpdated) onLeadUpdated(response.data.lead, response.data);
      await refreshAfterRun();
      const result = latestRunAllResult(response?.data?.run_all_result);
      const nextLabel = response?.data?.next_stage?.label || titleize(response?.data?.lead?.current_stage || 'next stage');
      const detail = result?.message || `Advanced to ${nextLabel}`;
      if (result?.status === 'blocked') {
        showToast({ severity: 'warn', summary: 'Pipeline Paused', detail });
      } else if (result?.status === 'started' || result?.status === 'running' || result?.status === 'waiting') {
        showToast({ severity: 'info', summary: 'Pipeline Running', detail });
      } else {
        showToast({ severity: 'success', summary: 'Continued', detail });
      }
    } catch (error) {
      console.error('Failed to resolve staff review:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to resolve staff review' });
    } finally {
      setRunningAction('');
    }
  };

  const upgradePipeline =

  async () => {
    if (!latestPipelineVersion?.id) return;
    const versionLabel = latestPipelineVersion.version_number ? `v${latestPipelineVersion.version_number}` : `#${latestPipelineVersion.id}`;
    const reason = `Manual pipeline upgrade to ${versionLabel}`;
    setRunningAction('upgrade');
    try {
      const response = await updateLeadPipelineVersion(lead.id, { pipeline_version_id: latestPipelineVersion.id, reason });
      if (response.data.lead && onLeadUpdated) onLeadUpdated(response.data.lead);
      showToast({ severity: 'success', summary: 'Updated', detail: 'Lead pipeline version updated' });
      await refreshAfterRun();
    } catch (error) {
      console.error('Failed to upgrade intake pipeline version:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to upgrade pipeline version' });
    } finally {
      setRunningAction('');
    }
  };

  useEffect(

  () => {
    if (!pipelineLoaded) return undefined;
    let active = true;
    const leadChanged = outreachLeadIdRef.current !== lead.id;
    const formLead = {
      prospect_name: lead.prospect_name,
      prospect_email: lead.prospect_email,
      prospect_phone: lead.prospect_phone
    };
    outreachLeadIdRef.current = lead.id;

    if (leadChanged) {
      setLoadingOutreach(true);
      setRows([]);
      setSelectedId(null);
      selectedIdRef.current = null;
      setForm(getDefaultOutreachForm(formLead));
      setDeliveryPolicy(null);
    }

    fetchIntakeOutreach(lead.id)
    .then(

    (response) => {
      if (!active) return;
      const outreach = response.data.outreach || [];
      setDeliveryPolicy(response.data.delivery_policy || null);
      setRows(outreach);
      const currentSelectedId = selectedIdRef.current;
      if (currentSelectedId === 'new') return;
      const selectedStillExists = currentSelectedId && outreach.some((row) => row.id === currentSelectedId);
      if (selectedStillExists) return;
      const defaultRow = outreach.find((row) => row.message_type === 'email' && EDITABLE_OUTREACH_STATUSES.has(row.status));
      if (defaultRow) {
        selectedIdRef.current = defaultRow.id;
        setSelectedId(defaultRow.id);
        setForm(buildOutreachForm(formLead, defaultRow));
      } else if (currentSelectedId) {
        selectedIdRef.current = null;
        setSelectedId(null);
        setForm(getDefaultOutreachForm(formLead));
      }
    })
    .catch((error) => {
      console.error('Failed to load intake outreach:', error);
      if (active) showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load outreach drafts' });
    })
    .finally(() => {
      if (active) setLoadingOutreach(false);
    });

    return () => {
      active = false;
    };
  }, [lead.id, lead.prospect_email, lead.prospect_name, lead.prospect_phone, pipelineLoaded, runRefreshKey, showToast]);

  const selectRow =

  (row) => {
    setSelectedId(row.id);
    setForm(buildOutreachForm(lead, row));
  };

  const startNewDraft =

  () => {
    setSelectedId('new');
    setForm(getDefaultOutreachForm(lead));
  };

  const mergeSavedRow =

  (savedRow, select = true) => {
    setRows((current) => {
      const exists = current.some((row) => row.id === savedRow.id);
      if (!exists) return [savedRow].concat(current);
      return current.map((row) => row.id === savedRow.id ? savedRow : row);
    });
    if (select) {
      setSelectedId(savedRow.id);
      setForm(buildOutreachForm(lead, savedRow));
    }
  };

  const saveCurrentDraft =

  async (silent = false) => {
    if (!editorActive || editorLocked) return null;
    setSaving(true);
    try {
      const payload = buildOutreachPayload(form);
      const response = selectedRow ?
      await updateIntakeOutreach(lead.id, selectedRow.id, payload) :
	      await createIntakeOutreach(lead.id, payload);
	      const savedRow = response.data.outreach;
	      if (response.data.delivery_policy) setDeliveryPolicy(response.data.delivery_policy);
	      mergeSavedRow(savedRow);
      if (!silent) showToast({ severity: 'success', summary: 'Saved', detail: 'Outreach draft saved' });
      return savedRow;
    } catch (error) {
      console.error('Failed to save intake outreach:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to save outreach draft' });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const sendCurrentDraft =

  async (sendTarget) => {
    const canSendTarget = sendTarget === 'dev' ? canSendToDev : canSendToActual;
    if (!canSendTarget) return;
    const savedRow = await saveCurrentDraft(true);
    if (!savedRow) return;

    setSendingId(savedRow.id);
    setSendingTarget(sendTarget);
    try {
	      const response = await sendIntakeOutreach(lead.id, savedRow.id, { send_target: sendTarget });
	      const sentRow = response.data.outreach;
	      const sentPolicy = response.data.delivery_policy || response.data.delivery || deliveryPolicy;
	      if (response.data.delivery_policy) setDeliveryPolicy(response.data.delivery_policy);
	      if (response.data.lead) {
	        onLeadUpdated?.(response.data.lead);
	      } else {
	        onRefreshLead?.();
	      }
	      mergeSavedRow(sentRow, false);
	      setSelectedId(null);
	      setForm(getDefaultOutreachForm(lead));
	      const workflowSummary = response.data.workflow_summary || response.data.lead?.workflow_summary;
	      if (workflowSummary?.state === 'waiting_on_prospect' && sentPolicy?.live) {
	        showToast({ severity: 'info', summary: 'Pipeline Wait Started', detail: 'Information request stage started. Waiting for reply, upload, or timeout.' });
	      } else {
	        showToast(getOutreachSendToast(sentPolicy, form.recipient_email));
	      }
	    } catch (error) {
	      console.error('Failed to send intake outreach:', error);
	      const failedRow = error?.response?.data?.outreach;
	      if (error?.response?.data?.delivery_policy) setDeliveryPolicy(error.response.data.delivery_policy);
	      if (failedRow) mergeSavedRow(failedRow);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to send outreach email' });
    } finally {
      setSendingId(null);
      setSendingTarget(null);
    }
  };

  const skipCurrentDraft =

  async () => {
    if (!selectedRow || editorLocked) return;
	    setSaving(true);
	    try {
	      const response = await updateIntakeOutreach(lead.id, selectedRow.id, { status: 'skipped' });
	      if (response.data.delivery_policy) setDeliveryPolicy(response.data.delivery_policy);
	      mergeSavedRow(response.data.outreach, false);
      setSelectedId(null);
      setForm(getDefaultOutreachForm(lead));
      showToast({ severity: 'success', summary: 'Skipped', detail: 'Outreach draft skipped' });
    } catch (error) {
      console.error('Failed to skip intake outreach:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to skip outreach draft' });
    } finally {
      setSaving(false);
    }
  };

  const draftControls = readOnly ? null : (
            <div className="intake-pipeline-control">
            <div className="intake-section-heading">
                <div>
                    <h2>Email Drafts</h2>
                    <span>{draftRows.length ? `${draftRows.length} ready for staff review` : 'No open drafts'}</span>
                </div>
                {!showEmptyDraftPrompt &&
                <div className="intake-action-row">
                    <button type="button" className="intake-button" onClick={startNewDraft}>
                        <AppIcon icon="pi pi-plus" aria-hidden="true" />
                        <span>New Draft</span>
                    </button>
                </div>
                }
            </div>
            {showEmptyDraftPrompt ?
      <div className="intake-draft-empty-action">
                    <div>
                        <strong>No open email drafts</strong>
                        <span>Create a draft for the next prospect outreach.</span>
                    </div>
                    <button type="button" className="intake-button intake-button--primary" onClick={startNewDraft}>
                        <AppIcon icon="pi pi-plus" aria-hidden="true" />
                        <span>New Draft</span>
                    </button>
                </div> :
            <div className={draftGridClassName}>
                {(loadingOutreach || draftRows.length > 0) &&
                <div className="intake-draft-queue" aria-label="Email draft queue">
                    {loadingOutreach && !draftRows.length && <EmptyState title="Loading drafts..." />}
                    {draftRows.map((row) =>
        <button
          type="button"
          className={`intake-draft-card${row.id === selectedId ? ' intake-draft-card--selected' : ''}`}
          key={row.id}
          onClick={() => selectRow(row)}>

                            <div className="intake-draft-card__main">
                                <strong>{row.subject || titleize(row.message_type)}</strong>
                                <span>{row.recipient_email || row.recipient_phone || row.recipient_name || '-'}</span>
                                <small>{getDraftSubtitle(row)}</small>
                            </div>
                            <WorkflowBadge status={row.status} />
                        </button>
        )}
                </div>
                }

                {editorActive &&
                <form className="intake-draft-editor" onSubmit={(event) => {event.preventDefault();saveCurrentDraft();}}>
                    <>
                            <div className="intake-form-grid">
                                <SelectInput label="Type" value={form.message_type} disabled={editorLocked || saving || sendingId} onChange={(value) => setForm((current) => ({ ...current, message_type: value }))} options={[['email', 'Email'], ['sms', 'SMS'], ['phone', 'Phone'], ['internal', 'Internal']]} />
                                <TextInput label="Recipient" value={form.recipient_name} disabled={editorLocked || saving || sendingId} onChange={(value) => setForm((current) => ({ ...current, recipient_name: value }))} />
                                <TextInput label="To" value={form.recipient_email} disabled={editorLocked || saving || sendingId} onChange={(value) => setForm((current) => ({ ...current, recipient_email: value }))} />
                                <TextInput label="Phone" value={form.recipient_phone} disabled={editorLocked || saving || sendingId} onChange={(value) => setForm((current) => ({ ...current, recipient_phone: value }))} />
                                <TextInput label="Subject" value={form.subject} disabled={editorLocked || saving || sendingId} onChange={(value) => setForm((current) => ({ ...current, subject: value }))} />
	                                <TextArea label="Requested Info" value={form.requested_info} rows={3} disabled={editorLocked || saving || sendingId} onChange={(value) => setForm((current) => ({ ...current, requested_info: value }))} />
	                                <TextArea label="Message" value={form.body} rows={8} disabled={editorLocked || saving || sendingId} onChange={(value) => setForm((current) => ({ ...current, body: value }))} />
	                            </div>
	                            {deliveryNotice && form.message_type === 'email' &&
	                            <div className={`intake-draft-delivery-notice intake-draft-delivery-notice--${deliveryNotice.tone}`}>
	                                <AppIcon icon={deliveryNotice.icon} aria-hidden="true" />
	                                <div>
	                                    <strong>{deliveryNotice.title}</strong>
	                                    <span>{deliveryNotice.detail}</span>
	                                </div>
	                            </div>}
	                            {selectedRow?.send_error && <div className="intake-draft-editor__error">{selectedRow.send_error}</div>}
	                            <div className="intake-draft-editor__actions">
                                <button type="submit" className="intake-button" disabled={saving || sendingId || editorLocked}>
                                    <AppIcon icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-save'} aria-hidden="true" />
                                    <span>{saving ? 'Saving' : 'Save Draft'}</span>
                                </button>
	                                <button type="button" className="intake-button" disabled={!canSendToDev} onClick={() => sendCurrentDraft('dev')}>
	                                    <AppIcon icon={sendingTarget === 'dev' ? 'pi pi-spin pi-spinner' : 'pi pi-send'} aria-hidden="true" />
	                                    <span>{sendingTarget === 'dev' ? 'Sending' : 'Send to Dev Account'}</span>
	                                </button>
	                                <button type="button" className="intake-button intake-button--primary" disabled={!canSendToActual} onClick={() => sendCurrentDraft('actual')}>
	                                    <AppIcon icon={sendingTarget === 'actual' ? 'pi pi-spin pi-spinner' : 'pi pi-send'} aria-hidden="true" />
	                                    <span>{sendingTarget === 'actual' ? 'Sending' : 'Send to Actual Person'}</span>
	                                </button>
                                <button type="button" className="intake-button" disabled={!selectedRow || saving || sendingId || editorLocked} onClick={skipCurrentDraft}>
                                    <AppIcon icon="pi pi-ban" aria-hidden="true" />
                                    <span>Skip</span>
                                </button>
                            </div>
                        </>
                </form>
                }
                {!editorActive && draftRows.length > 0 && <div className="intake-draft-placeholder">Select a draft to review or edit.</div>}
            </div>}
        </div>);

  return (
    <div className="intake-tab-panel intake-stack">
            {loading && !pipelineRuns.length && !stageRuns.length && !agentRuns.length &&
            <div className="intake-loading">Loading pipeline...</div>}
            {error && !pipelineRuns.length && !stageRuns.length && !agentRuns.length &&
            <div className="intake-inline-alert" role="alert"><span>{error}</span></div>}
            <section className={overviewClassName}>
                <div className="intake-pipeline-overview__main">
                    <span className="intake-pipeline-eyebrow">Pipeline</span>
                    <h2>{currentStage?.label || getStageLabel(lead.current_stage)}</h2>
                    <p>{workflowMessage}</p>
                    <div className="intake-pipeline-progress">
                        {workflowTimelineStageCount} stage{workflowTimelineStageCount === 1 ? '' : 's'} in route
                    </div>
                </div>
	                <div className={`intake-pipeline-overview__decision intake-pipeline-overview__decision--${decisionBrief.tone}`}>
	                    <span className="intake-pipeline-eyebrow">Decision Brief</span>
                    {decisionBrief.hasRecommendation ?
                    <span className={`intake-final-recommendation-badge intake-final-recommendation-badge--${decisionBrief.tone}`}>{decisionBrief.title}</span> :
                    <strong>{decisionBrief.title}</strong>}
	                    <p>{decisionBrief.detail}</p>
                    <small>{decisionBrief.meta}</small>
                    <small>{pipelineVersion ? `Pipeline v${pipelineVersion.version_number}` : 'No pinned pipeline version'}{latestPipelineVersion && pipelineVersion?.id !== latestPipelineVersion.id ? ` / latest v${latestPipelineVersion.version_number}` : ''}</small>
	                </div>
                {showSpending &&
                <div className="intake-pipeline-overview__spending">
                    <span className="intake-pipeline-eyebrow">AI Spend</span>
                    <strong>{formatEstimatedCost(aiSpending?.estimated_cost, aiSpending?.currency || 'usd')}</strong>
                    <p>{formatAiSpendingActivity(aiSpending)}</p>
                    <small>{formatAiProviderBreakdown(aiSpending)}</small>
                </div>}
	            </section>

            {canUpgradePipeline &&
            <section className="intake-pipeline-control">
                <div className="intake-section-heading">
                    <div>
                        <h2>Pipeline Version</h2>
                        <span>Move this lead to the latest published pipeline when needed.</span>
                    </div>
                    <div className="intake-action-row">
	                        <button type="button" className="intake-button" disabled={Boolean(runningAction)} onClick={upgradePipeline}>
	                            <AppIcon icon={runningAction === 'upgrade' ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-up'} aria-hidden="true" />
	                            <span>Upgrade Pipeline</span>
	                        </button>
	                    </div>
	                </div>
	            </section>}

	            <section>
		                <div className="intake-section-heading">
                    <div>
	                        <h2>Stage Timeline</h2>
                            <span>Stage status and outputs</span>
                    </div>
                    <div className="intake-action-row">
                        <button
                          type="button"
                          className="intake-button intake-button--primary"
                          disabled={Boolean(runningAction) || hasActivePipelineWork || !canRunRemainingPipeline}
                          title={runRemainingButtonTitle}
                          onClick={runRemainingPipeline}>
                            <AppIcon icon={runRemainingButtonRunning ? 'pi pi-spin pi-spinner' : 'pi pi-forward'} aria-hidden="true" />
                            <span>{runRemainingButtonLabel}</span>
                        </button>
                        <details className="intake-view-menu">
                            <summary className="intake-button" aria-label="View options">
                                <AppIcon icon="pi pi-sliders-h" aria-hidden="true" />
                                <span>View</span>
                            </summary>
                            <div className="intake-view-menu__panel">
                                <label className="intake-checkbox-row intake-spending-toggle">
                                    <input type="checkbox" checked={showSpending} onChange={handleSpendingToggle} />
                                    <span>Spending</span>
                                </label>
                                <label className="intake-checkbox-row intake-legal-verification-toggle">
                                    <input type="checkbox" checked={showLegalVerification} onChange={handleLegalVerificationToggle} />
                                    <span>Legal Verification</span>
                                </label>
                                <label className="intake-checkbox-row intake-agent-controls-toggle" title="Show per-agent debug controls (agent runs, context, feedback, manual reruns) inside each stage.">
                                    <input type="checkbox" checked={showAgentControls} onChange={handleAgentControlsToggle} />
                                    <span>Agent Controls</span>
                                </label>
                                <label className="intake-checkbox-row intake-runtime-detail-toggle" title="Show internal pipeline runtime signals, commands, timers, gates, effects, and steps.">
                                    <input type="checkbox" checked={showRuntimeDetail} onChange={handleRuntimeDetailToggle} />
                                    <span>Runtime Detail</span>
                                </label>
                            </div>
                        </details>
                    </div>
	                </div>
                <StageAccordion
          stageItems={workflowStageItems}
          timelineEntries={workflowTimelineEntries}
          currentStageKey={currentStageRowKey}
          stageActionItems={stageActionItems}
          activeRunCountsByStage={activeRunCountsByStage}
          informationRequestLoopGuidance={informationRequestLoopGuidance}
          showSpending={showSpending}
          onRunStage={runStage}
          onSkipStage={skipStage}
          runningAction={runningAction}
          renderStageContent={(stage, isCurrent) =>
          <StageSpecificPanel
            stage={stage}
            lead={lead}
            agentRuns={agentRuns}
            isCurrent={isCurrent}
	            draftControls={draftControls}
		            onRunStage={runStage}
		            onRetryAgentRun={retryAgentRun}
		            onContinueInformationEvaluation={continueInformationEvaluation}
		            onResolveStaffReview={resolveStaffReview}
		            onOpenAgentContext={openAgentContext}
	            onOpenAgentFeedback={openAgentFeedback}
	            onRefreshEngagementPackage={() => Promise.all([
	              onRefreshLead ? onRefreshLead() : Promise.resolve(),
	              onRefreshPipeline ? onRefreshPipeline() : Promise.resolve(),
	            ])}
	            runningAction={runningAction}
	            loadingContextRunId={loadingContextRunId}
	            loadingFeedbackRunId={loadingFeedbackRunId}
	            selectedClaimSpecialistKeys={selectedClaimSpecialistKeys}
	            modelProfiles={modelProfiles}
	            manualModelProfileSelections={manualModelProfileSelections}
	            onModelProfileChange={setManualModelProfileSelection}
	            onOpenDocuments={onOpenDocuments}
	            onOpenDecision={onOpenDecision}
	            showLegalVerification={showLegalVerification}
            showAgentControls={!readOnly && showAgentControls} />}
           />
            </section>
            {showRuntimeDetail && <WorkflowRuntimeSection leadId={lead.id} />}
            <AgentRunDetailModal
              detail={agentContextDetail}
              onClose={closeAgentContextDetail}
              mode="context"
              showLegalVerification={showLegalVerification && agentRunAllowsLegalVerification(agentContextDetail)} />
            <AgentFeedbackModal
              target={agentFeedbackTarget}
              feedbackItems={agentFeedbackItems}
              loading={agentFeedbackLoading}
              saving={agentFeedbackSaving}
              onSubmit={submitAgentFeedback}
              onDeactivate={deactivateAgentFeedback}
              onClose={closeAgentFeedback} />
        </div>);

}



function InformationRequestLoopGuidance({ guidance, onSkipStage, runningAction }) {
  if (!guidance) return null;
  const isSkipping = guidance.action === 'skip_information_request' && runningAction === `skip-stage:${guidance.stage?.key}`;
  return (
    <div className={`intake-pipeline-loop-guidance intake-pipeline-loop-guidance--${guidance.tone || 'info'}`}>
      <AppIcon icon={guidance.icon || 'pi pi-info-circle'} aria-hidden="true" />
      <div className="intake-pipeline-loop-guidance__body">
        <strong>{guidance.title}</strong>
        <span>{guidance.detail}</span>
      </div>
      {guidance.action === 'skip_information_request' &&
        <button
          type="button"
          className="intake-button"
          disabled={Boolean(runningAction)}
          onClick={() => onSkipStage(guidance.stage)}>
          <AppIcon icon={isSkipping ? 'pi pi-spin pi-spinner' : 'pi pi-forward'} aria-hidden="true" />
          <span>{guidance.actionLabel || 'Skip to Document Review'}</span>
        </button>}
    </div>
  );
}



function getStageOutputId(rowKey) {
  return `intake-stage-output-${String(rowKey || 'stage').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}



function StageHeaderStateList({ states = [], stageLabel = 'Stage' }) {
  if (!states.length) return null;
  return (
    <div className="intake-pipeline-stage__state-list" aria-label={`${stageLabel} states`}>
      {states.map((item) =>
        <span
          className={`intake-pipeline-stage-state intake-pipeline-stage-state--${item.tone || 'neutral'}`}
          key={item.label}
          title={item.title || item.label}>
          {item.label}
        </span>
      )}
    </div>
  );
}



function StageAccordion({ stageItems, timelineEntries, currentStageKey, stageActionItems, activeRunCountsByStage, informationRequestLoopGuidance, showSpending, onRunStage, onSkipStage, runningAction, renderStageContent }) {
  const entries = timelineEntries || buildStageTimelineEntries(stageItems);
  const outputPaneRef = useRef(null);
  const stageOutputRefs = useRef({});
  const [activeStageKey, setActiveStageKey] = useState(currentStageKey || '');

  useEffect(() => {
    if (currentStageKey) setActiveStageKey(currentStageKey);
  }, [currentStageKey]);

  const registerStageOutput = useCallback(

  (rowKey, node) => {
    if (!rowKey) return;
    if (node) stageOutputRefs.current[rowKey] = node;
    else delete stageOutputRefs.current[rowKey];
  }, []);

  const scrollStageIntoView = useCallback(

  (rowKey) => {
    const target = stageOutputRefs.current[rowKey];
    if (!target) return;
    const pane = outputPaneRef.current;
    const prefersReducedMotion = Boolean(
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';
    if (pane && typeof pane.scrollTo === 'function' && typeof target.getBoundingClientRect === 'function') {
      const paneRect = pane.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top = targetRect.top - paneRect.top + pane.scrollTop - 8;
      pane.scrollTo({ top: Math.max(top, 0), behavior });
      return;
    }
    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior, block: 'start' });
    }
  }, []);

  const activateStage = useCallback(

  (stage) => {
    const rowKey = stage.rowKey || stage.key;
    setActiveStageKey(rowKey);
    window.setTimeout(() => scrollStageIntoView(rowKey), 0);
  }, [scrollStageIntoView]);

  // Only one timeline entry is rendered at a time; the nav switches the active output.
  const activeKey = activeStageKey || currentStageKey || '';
  const entryContainsKey =

  (entry, key) => entry.type === 'information_request_loop' ?
    entry.stages.some((stage) => (stage.rowKey || stage.key) === key) :
    (entry.stage.rowKey || entry.stage.key) === key;
  const matchedEntryIndex = entries.findIndex((entry) => entryContainsKey(entry, activeKey));
  const activeEntryIndex = matchedEntryIndex >= 0 ? matchedEntryIndex : entries.length - 1;
  const activeEntry = entries[activeEntryIndex];

  const renderStageNavItem =

  (stage, displayIndex, nestedIndex = null) => {
    const rowKey = stage.rowKey || stage.key;
    const isActive = activeStageKey ? activeStageKey === rowKey : rowKey === currentStageKey;
    const indexLabel = nestedIndex === null ? displayIndex : `${displayIndex}.${nestedIndex + 1}`;
    const className = [
      'intake-pipeline-stage-nav__item',
      `intake-pipeline-stage-nav__item--${stage.state.className}`,
      isActive ? 'intake-pipeline-stage-nav__item--active' : '',
      nestedIndex !== null ? 'intake-pipeline-stage-nav__item--nested' : ''
    ].filter(Boolean).join(' ');
    return (
      <button
        type="button"
        className={className}
        key={`nav-${rowKey}`}
        aria-current={isActive ? 'step' : undefined}
        aria-controls={getStageOutputId(rowKey)}
        aria-label={`${stage.label} output`}
        onClick={() => activateStage(stage)}>
        <span className="intake-pipeline-stage-nav__index">{indexLabel}</span>
        <span className="intake-pipeline-stage-nav__body">
          <strong>{stage.label}</strong>
          <small>{stage.meta}</small>
        </span>
        <span className={`intake-pipeline-status intake-pipeline-status--${stage.state.className}`}>{stage.state.label}</span>
      </button>
    );
  };

  const getActiveStageForEntry =

  (entry) => {
    if (!entry) return null;
    if (entry.type !== 'information_request_loop') {
      return { stage: entry.stage, displayIndex: entry.displayIndex, nestedIndex: null };
    }
    const stageIndex = entry.stages.findIndex((stage) => (stage.rowKey || stage.key) === activeKey);
    const currentIndex = entry.stages.findIndex((stage) => (stage.rowKey || stage.key) === currentStageKey);
    const nestedIndex = stageIndex >= 0 ? stageIndex : currentIndex >= 0 ? currentIndex : 0;
    return {
      stage: entry.stages[nestedIndex],
      displayIndex: entry.displayIndex,
      nestedIndex
    };
  };

  const renderActiveStage =

  (stage, displayIndex, nestedIndex = null) => {
    if (!stage) return null;
    const rowKey = stage.rowKey || stage.key;
    const isCurrent = rowKey === currentStageKey;
    const actions = stageActionItems[stage.key] || [];
    const actionKey = `stage:${stage.key}`;
    const skipActionKey = `skip-stage:${stage.key}`;
    const isPrimaryStageAction = stage.showRunButton && !stage.readinessBlocked && !stage.aiProcessingLocked;
    const stageRunTitle = !stage.canRun ?
      stage.runDisabledReason || (stage.closed ? 'Lead is closed.' : stage.readiness?.reason || 'Run previous stages first.') :
      stage.key === 'document_review' ?
        'Runs Document Review on pending/unreviewed documents only. Reviewed documents stay out of this batch.' :
        'Runs the full stage with pipeline-configured model defaults.';
    const stageSkipTitle = stage.canSkipStage ?
      `Skip ${stage.label || 'Stage'} and advance to the next stage.` :
      stage.skipDisabledReason || 'Skip is not available for this stage.';
    const runButtonClassName = [
      'intake-button',
      'intake-pipeline-stage__run-button',
      isPrimaryStageAction ? 'intake-pipeline-stage__run-button--primary' : '',
      stage.readinessBlocked || stage.aiProcessingLocked ? 'intake-pipeline-stage__run-button--locked' : ''
    ].filter(Boolean).join(' ');
    const skipButtonClassName = [
      'intake-button',
      'intake-pipeline-stage__run-button',
      'intake-pipeline-stage__run-button--revert'
    ].join(' ');
    const indexLabel = nestedIndex === null ? displayIndex : `${displayIndex}.${nestedIndex + 1}`;
    const stageSpend = stage.run?.ai_spending || null;
    const stageSpendLabel = formatEstimatedCost(stageSpend?.estimated_cost, stageSpend?.currency || 'usd');
    const stageSpendTitle = `AI spend ${stageSpendLabel}. ${formatAiSpendingActivity(stageSpend)}.`;
    return (
      <section
        id={getStageOutputId(rowKey)}
        ref={(node) => registerStageOutput(rowKey, node)}
        className={`intake-pipeline-stage intake-pipeline-stage--${stage.state.className}`}
        key={rowKey}>
        <div className="intake-pipeline-stage__summary">
          <div className="intake-pipeline-stage__index">{indexLabel}</div>
          <div className="intake-pipeline-stage__body">
            <strong>{stage.label}</strong>
            <span>{stage.description}</span>
          </div>
          <div className="intake-pipeline-stage__meta">
            {showSpending && stage.run &&
              <span className="intake-pipeline-stage__cost" aria-label={`AI spend ${stageSpendLabel}`} title={stageSpendTitle}>
                {stageSpendLabel}
              </span>}
            {stage.state.className !== 'current' &&
              <span className={`intake-pipeline-status intake-pipeline-status--${stage.state.className}`}>{stage.state.label}</span>}
            <StageHeaderStateList states={stage.headerStates} stageLabel={stage.label} />
            <small>{stage.meta}</small>
            {stage.showRunButton &&
              <button
                type="button"
                className={runButtonClassName}
                disabled={Boolean(runningAction) || !stage.canRun}
                title={stageRunTitle}
                onClick={

                (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!stage.canRun) return;
                  onRunStage(stage);
                }}>
                <AppIcon icon={runningAction === actionKey || stage.stageRunning ? 'pi pi-spin pi-spinner' : !stage.canRun ? 'pi pi-lock' : 'pi pi-play'} aria-hidden="true" />
                <span>{stage.runActionLabel}</span>
              </button>}
            {stage.showSkipButton &&
              <button
                type="button"
                className={skipButtonClassName}
                disabled={Boolean(runningAction) || !stage.canSkipStage}
                title={stageSkipTitle}
                onClick={

                (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!stage.canSkipStage) return;
                  onSkipStage(stage);
                }}>
                <AppIcon icon={runningAction === skipActionKey ? 'pi pi-spin pi-spinner' : 'pi pi-forward'} aria-hidden="true" />
                <span>Skip Stage</span>
              </button>}
          </div>
        </div>
        <div className="intake-pipeline-stage__panel">
          <StageRunActivity counts={activeRunCountsByStage[rowKey]} starting={runningAction === actionKey && isCurrent} />
          <StageActionList actions={actions} empty={isCurrent && stage.key !== OUTREACH_RESPONSE_MONITOR_STAGE_KEY ? 'No previous actions for this step.' : ''} />
          {renderStageContent(stage, isCurrent)}
        </div>
      </section>);
  };

  // With a single timeline entry the nav rail would only repeat the stage card.
  const showStageNav = entries.length > 1;
  const activeStage = getActiveStageForEntry(activeEntry);
  const showLoopGuidance = activeEntry?.type === 'information_request_loop' && activeStage?.stage?.rowKey === currentStageKey;

  return (
    <div className={`intake-pipeline-dual-pane${showStageNav ? '' : ' intake-pipeline-dual-pane--single'}`}>
      {showStageNav &&
      <nav className="intake-pipeline-stage-nav" aria-label="Intake pipeline stages">
        <div className="intake-pipeline-stage-nav__header">
          <span className="intake-pipeline-eyebrow">Stages</span>
        </div>
        <div className="intake-pipeline-stage-nav__list">
          {entries.map((entry) => {
            if (entry.type !== 'information_request_loop') {
              return renderStageNavItem(entry.stage, entry.displayIndex);
            }
            const loopSummary = getInformationRequestLoopSummary(entry.stages);
            return (
              <div className="intake-pipeline-stage-nav__group" key={`nav-${entry.key}`}>
                <div className="intake-pipeline-stage-nav__group-header">
                  <span className="intake-pipeline-stage-nav__index">{entry.displayIndex}</span>
                  <div className="intake-pipeline-stage-nav__group-body">
                    <strong>Information Request Loop</strong>
                    <small>{loopSummary}</small>
                  </div>
                </div>
                <div className="intake-pipeline-stage-nav__nested-list intake-pipeline-stage-nav__nested-list--compact">
                  {entry.stages.map((stage, nestedIndex) => renderStageNavItem(stage, entry.displayIndex, nestedIndex))}
                </div>
              </div>
            );
          })}
        </div>
      </nav>}

      <div className="intake-pipeline-stage-output-pane" ref={outputPaneRef} aria-label="Stage outputs">
        <div className="intake-pipeline-stage-list">
          {showLoopGuidance &&
            <InformationRequestLoopGuidance guidance={informationRequestLoopGuidance} onSkipStage={onSkipStage} runningAction={runningAction} />}
          {activeStage && renderActiveStage(activeStage.stage, activeStage.displayIndex, activeStage.nestedIndex)}
        </div>
      </div>
    </div>);

}



function StageActionList({ actions, empty }) {
  if (!actions.length) {
    return empty ? <div className="intake-stage-action-empty">{empty}</div> : null;
  }

  return (
    <div className="intake-stage-action-list">
            {actions.map((item) => <StageActionRow item={item} key={item.id} />)}
        </div>);

}



function StageActionSummary({ item, expandable = false }) {
  return (
    <>
      <time>{item.time ? formatDate(item.time) : 'No timestamp'}</time>
      <div>
        <strong>{item.label}</strong>
        <span>{item.detail}</span>
      </div>
      <div className="intake-stage-action-row__meta">
        <IntakeBadge value={item.statusLabel || item.status} className={getRecordStatusClass(item.status)} />
        {expandable && <AppIcon icon="pi pi-chevron-down" className="intake-stage-action-row__chevron" aria-hidden="true" />}
      </div>
    </>
  );
}



function StageActionDetail({ item }) {
  const outreach = item.outreach || {};
  const fields = outreach.detailFields || [];
  return (
    <div className="intake-stage-action-detail">
      {fields.length > 0 &&
      <div className="intake-stage-action-detail__grid">
        {fields.map(([label, value]) =>
        <div className="intake-stage-action-detail__field" key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
        )}
      </div>}
      {outreach.subject &&
      <div className="intake-stage-action-detail__body">
        <span>Subject</span>
        <pre>{outreach.subject}</pre>
      </div>}
      {outreach.body &&
      <div className="intake-stage-action-detail__body">
        <span>Body</span>
        <pre>{outreach.body}</pre>
      </div>}
      {outreach.requestedInfo &&
      <div className="intake-stage-action-detail__body">
        <span>Requested info</span>
        <pre>{outreach.requestedInfo}</pre>
      </div>}
    </div>
  );
}



function StageActionRow({ item }) {
  const expandable = Boolean(item.outreach);
  if (!expandable) {
    return (
      <div className="intake-stage-action-row">
        <StageActionSummary item={item} />
      </div>
    );
  }

  return (
    <details className="intake-stage-action-row intake-stage-action-row--expandable">
      <summary className="intake-stage-action-row__summary">
        <StageActionSummary item={item} expandable />
      </summary>
      <StageActionDetail item={item} />
    </details>
  );
}



function IntakeTabs({
  lead,
  pipelineRuns,
  stageRuns,
  agentRuns,
  finalRecommendations = [],
  aiSpending = null,
	  pipelineVersion,
	  latestPipelineVersion,
	  pipelineStages,
	  workflowRail = {},
	  modelProfiles = [],
	  canUpgradePipeline,
  pipelineLoaded = false,
  loading = false,
  error = '',
  engagementLetterHandoff = false,
	  onLeadUpdated,
  onRefreshLead,
  onRefreshPipeline,
  onOpenDecision = () => {},
  completedMode = false,
  presentationState = 'active',
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const conflictChecksEnabled = useConflictChecksEnabled();

  useEffect(() => {
    if (activeIndex !== 0 || pipelineLoaded || loading || !onRefreshPipeline) return undefined;
    onRefreshPipeline();
    return undefined;
  }, [activeIndex, loading, onRefreshPipeline, pipelineLoaded]);

  return (
    <div className="intake-lead-object-workspace">
        <AppTabView activeIndex={activeIndex} onTabChange={(event) => setActiveIndex(event.index)} variant="framed" className="intake-tabs">
            <AppTab header="Pipeline">
                {completedMode ? <CompletedIntakeRecord lead={lead} /> : <PipelineTab
          lead={lead}
          pipelineRuns={pipelineRuns}
          stageRuns={stageRuns}
          agentRuns={agentRuns}
          finalRecommendations={finalRecommendations}
          aiSpending={aiSpending}
          engagementLetterHandoff={engagementLetterHandoff}
          onOpenDocuments={() => setActiveIndex(1)}
	          pipelineVersion={pipelineVersion}
	          latestPipelineVersion={latestPipelineVersion}
		          pipelineStages={pipelineStages}
		          workflowRail={workflowRail}
	          modelProfiles={modelProfiles}
	          canUpgradePipeline={canUpgradePipeline}
          pipelineLoaded={pipelineLoaded}
          loading={loading}
          error={error}
          onLeadUpdated={onLeadUpdated}
          onRefreshLead={onRefreshLead}
          onRefreshPipeline={onRefreshPipeline}
          onOpenDecision={onOpenDecision}
	          readOnly={false} />}
            </AppTab>
            <AppTab header="Documents">
                <DocumentsTab lead={lead} active={activeIndex === 1} onRefreshLead={onRefreshLead} />
            </AppTab>
            <AppTab header="Activity History">
                <IntakeActivityWorkspace lead={lead} active={activeIndex === 2} />
            </AppTab>
            <AppTab header="Source Events">
                <SourceEventsTab leadId={lead.id} active={activeIndex === 3} />
            </AppTab>
            <AppTab header="Run Logs">
                <RunLogsTab leadId={lead.id} active={activeIndex === 4} />
            </AppTab>
            {conflictChecksEnabled && (
            <AppTab header="Conflicts">
                <div className="intake-tab-panel">
                    <ConflictCheckPanel subjectType="lead" leadId={lead.id} active={activeIndex === 5} />
                </div>
            </AppTab>
            )}
        </AppTabView>
    </div>);

}



function CompletedIntakeRecord({ lead }) {
  const matterNumber = lead?.converted_matter_number;
  return (
    <div className="intake-tab-panel intake-completed-record">
      <span className="intake-completed-record__eyebrow">Intake completed</span>
      <h2>{matterNumber ? 'Handed off to Matters' : 'Converted record'}</h2>
      {matterNumber ? (
        <>
          <p>This lead is now a Matter. Intake remains available here as history.</p>
          <Link className="intake-button intake-button--primary" to={`/case_details/${matterNumber}`}>Open matter</Link>
        </>
      ) : (
        <p>Converted in Pipedrive. No Internal Matter is linked to this imported record.</p>
      )}
    </div>
  );
}

export default IntakeTabs;
